import { mergeDropReleasedBoards } from './plannerDropReleased';
import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from 'react';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import { useMsal } from '@azure/msal-react';
import { TanTable, TabData, BulkEditMeta } from '../../components/TanTable';
import DateTimePicker from '../../components/DateTimePicker';
import Toggle from '../../components/Toggle';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import api from '../../services/api';
import { ColumnDef } from '@tanstack/react-table';
import ordersService, { Order, NotSentToGwiMoveOrder } from '../../services/orders.service';
import userSettingsService from '../../services/userSettings.service';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';

/** Registry fieldnames this board already renders under a column of its own, so
 *  the picker must not offer them a second time. Module-level for a stable
 *  identity — a fresh Set each render would re-run the picker memo. */
/** Stable identity on purpose — see useAdditionalFieldColumns. */
const isPlannerSubRow = (row: DispatchPlannerRow) => !!row.isSubRow;

const PLANNER_COVERED_FIELDS = {
  // Name + username both sit behind the one Dispatcher cell; two fieldnames name no
  // single title, so the column keeps its own unless a def matches its id.
  dispatcher: ['dispatchername', 'dispatcherusername'],
  billToName: ['billtoname'],
  customerName: ['customername'],
  region: ['revenueregion'],
} as const;
import DriverSelectorCell from '../../components/DriverSelectorCell';
import {
  mergeGridLayout,
  healLayoutHeights,
  readSavedCollapsed,
  orderMatchesLegTypes,
  partitionPlannerImports,
  placeMissingCards,
} from './dispatchPlannerLayout';
import { useVisiblePolling } from '../../hooks/useVisiblePolling';
import { needsDropReleaseWarning, DROP_RELEASE_WARNING_MESSAGE } from '../../utils/dropReleaseCheck';
import { needsNotReceivingWarning, NOT_RECEIVING_WARNING_MESSAGE } from '../../utils/notReceivingCheck';
import { needsNotRailbilledWarning, NOT_RAILBILLED_WARNING_MESSAGE } from '../../utils/notRailbilledCheck';
import { needsMissingPreadviseWarning, MISSING_PREADVISE_WARNING_MESSAGE } from '../../utils/missingPreadviseCheck';
import { openTroubleTicketWarning, blockingTroubleTicketMessage, legAtOrBeforeRejectedGroup } from '../../utils/openTroubleTicketCheck';
import { redeliveryExpirationWarning } from '../../utils/redeliveryExpirationCheck';
import { openRepairOrderWarning } from '../../utils/openRepairOrderCheck';
import { collectDragFillWarnings, WarnedPro } from '../../utils/dragFillWarnings';
import DragFillWarningModal from '../../components/DragFillWarningModal';
import { describeDispatchGroupDriverConflict } from '../../utils/driverMismatch';
import { useRealtimeCollaboration, ActiveUser } from '../../hooks/useRealtimeCollaboration';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { abbreviateLegType } from '../../utils/orderCardHelpers';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import { NotSentToGwiMoveCard } from '../../components/NotSentToGwiMoveCard';
import { usePermissions } from '../../contexts/PermissionContext';
import FilterBar from '../../components/FilterBar';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter } from '../../components/filters';
import { Tooltip } from '../../components/ui/Tooltip';
import { buildOpenTtColumn } from './openTtColumn';
import { timeOnBoardCell } from './timeOnBoardCell';
import { resolveLegApptDates, apptDateToIso } from './dispatchApptDates';
import { ExclamationTriangleIcon, ArrowTopRightOnSquareIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Users } from 'lucide-react';
import { config } from '../../config/config';

// One-shot migration: when this file was DispatchPlannerTest.tsx (the
// "Dispatch Planner Beta" route), the grid layout was saved under
// 'dispatch-planner-test-layout'. The Beta has now replaced the original
// planner, so move existing localStorage values onto the canonical key
// before the layout useState() initializers below read them. Idempotent,
// runs once per page load. Safe to delete after the rollout has aged out
// (~2026-07).
try {
  if (typeof window !== 'undefined') {
    const legacy = localStorage.getItem('dispatch-planner-test-layout');
    if (legacy && !localStorage.getItem('dispatch-planner-layout')) {
      localStorage.setItem('dispatch-planner-layout', legacy);
      localStorage.removeItem('dispatch-planner-test-layout');
    }
  }
} catch {
  // ignore quota / SecurityError (private mode, etc.) — defaults still load fine
}

// Apply a driver assignment to a single pro+leg in any state array.
// Returns prev unchanged if the pro+leg isn't present — safe to call across all
// three table arrays so a leg shared between Drop Released and Imports/Exports
// stays in sync.
//
// We match on pronumber AND legnum-in-legs because the same pronumber can map
// to multiple orders after the dispatch-group split (e.g. legs 2-3 in one
// order, leg 4 in another). Matching by pronumber alone would auto-fill the
// wrong row.
//
// The driver is cascaded to EVERY leg of the matched order, not just the leg
// the user typed into. The backend's _augment_orders_with_linked_legs makes
// each planner row represent exactly one dispatch group, so order.legs IS the
// group; and handleDispatchAll sends the same driver to every leg in legData
// anyway. Without the cascade, local state would show one leg assigned and
// the rest unassigned, which trips the "driver mismatch across dispatch
// group" tooltip even though the dispatch itself would be consistent.
const applyDriverAssignment = (prev: Order[], pronumber: number, legnum: number, driverUnit: string): Order[] => {
  const idx = prev.findIndex(o => o.pronumber === pronumber && (o.legs ?? []).some(l => l.legnum === legnum));
  if (idx === -1) return prev;
  const order = prev[idx];
  const updatedLegs = order.legs?.map(leg => ({ ...leg, driver: driverUnit }));
  const newOrders = [...prev];
  // Don't write to order.driver — that field conflates per-leg state at the
  // order level. Cells render driver from the matching leg directly.
  newOrders[idx] = { ...order, legs: updatedLegs };
  return newOrders;
};

// For multi-leg dispatch groups, expose every leg's filterable values as "related rows"
// so a column filter that matches any one leg keeps the whole group visible — without
// this, filtering Orig Type=SO on a group with legs (SO→D, D→SO) would hide the second leg.
// Only origType/destType vary per leg; other accessors are order-level and already shared.
const getDispatchGroupRelatedRows = (row: DispatchPlannerRow): DispatchPlannerRow[] => {
  if (!row.legs || row.legs.length <= 1) return [];
  return row.legs.map((leg) => ({
    ...row,
    origType: abbreviateLegType(leg.legtype),
    destType: abbreviateLegType(leg.legAction2),
  }));
};

// Sort empty/missing strings to the bottom regardless of sort direction.
// Default 'auto' sorting interleaves empty strings with valued rows in confusing ways.
const emptyLastStringSort = (rowA: any, rowB: any, columnId: string): number => {
  const a = (rowA.getValue(columnId) as string) || '';
  const b = (rowB.getValue(columnId) as string) || '';
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
};

// The backend builds city/state as "street address\ncity, state" (CONCAT with a
// newline). The planner only wants the city/state portion, so keep the text
// after the last newline (falls back to the whole value if there's no address).
const cityStateOnly = (v?: string): string => {
  if (!v) return '';
  const parts = v.split(/\r?\n/);
  return parts[parts.length - 1].trim();
};

// Location columns show the alias when set (the backend resolves cyalias-or-lname
// into *Name); the full name rides along in *Lname for hover + search. Compose the
// hover text: "ALIAS - Full Name" when an alias is shown, else just the full name.
const locationTooltipText = (display?: string, full?: string): string => {
  const d = (display || '').trim();
  const f = (full || '').trim();
  if (f && d && f !== d) return `${d} - ${f}`;
  return f || d;
};

// Origin location cell: alias-or-name with the full name on hover.
const renderOriginLocationCell = (row: DispatchPlannerRow): React.ReactNode => {
  const display = row.isSubRow ? row.legOriginName : row.originName;
  const full = row.isSubRow ? row.legOriginLname : row.originLname;
  const tip = locationTooltipText(display, full);
  return tip ? (
    <Tooltip content={tip} placement="top"><span>{display}</span></Tooltip>
  ) : (
    <span>{display}</span>
  );
};

// Destination location cell: alias-or-name + full name on hover.
const renderDestLocationCell = (row: DispatchPlannerRow): React.ReactNode => {
  const display = row.isSubRow ? row.legDestName : row.destinationName;
  const full = row.isSubRow ? row.legDestLname : row.destinationLname;
  const tip = locationTooltipText(display, full);
  const inner = <span>{display}</span>;
  return tip ? (
    <Tooltip content={tip} placement="top">{inner}</Tooltip>
  ) : (
    inner
  );
};

// New planner columns that ship available-but-hidden: users can add them via the
// column picker, but they don't show until chosen. Stable module-level identity
// so it doesn't retrigger TanTable's settings-load effect each render.
const PLANNER_DEFAULT_HIDDEN_COLUMNS = { bol: false, chassisPool: false, openTt: false };


// Context for the Drop Released Appt Date picker cells. Routing picked dates
// through Context (rather than closing the columns useMemo over the state)
// keeps the columns array identity stable across date picks — so driver cells
// don't re-render when the user types a date, which would otherwise stomp on
// in-progress driver validation in adjacent cells.
type PickedApptDatesContextValue = {
  pickedApptDates: Record<string, string>;
  setPickedApptDates: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};
const PickedApptDatesContext = createContext<PickedApptDatesContextValue | null>(null);

// Inline appt-date picker for the Drop Released table. Reads its own picked
// date from context so picking only re-renders other appt cells; the wider
// columns array (and therefore other cells like driver) stays stable.
//
// `width` comes from column.getSize() — the picker's parent chain is a block
// element with width:auto sitting inside a flex td, so `w-full` resolves to
// content-size (~95px) instead of the cell's 160+px. Setting explicit pixel
// width on the wrapper short-circuits that broken chain.
const ApptDateCell: React.FC<{
  pronumber: number;
  legnum: number;
  width: number;
  /**
   * The group's current appt date (ISO) to show when the user hasn't picked
   * one, so the cell still reads as "this is scheduled for X" while being
   * editable. Null when the group has no date yet.
   */
  currentIso: string | null;
}> = ({ pronumber, legnum, width, currentIso }) => {
  const ctx = useContext(PickedApptDatesContext);
  if (!ctx) return null;
  const { pickedApptDates, setPickedApptDates } = ctx;
  const key = `${pronumber}-${legnum}`;
  // Key presence — not truthiness — separates "user picked a date" from
  // "untouched". An untouched cell displays the current date but sends no
  // override, so simply opening the board can't rewrite anyone's dates.
  const isOverridden = key in pickedApptDates;
  const iso = isOverridden ? pickedApptDates[key] : currentIso;
  // Parse 'YYYY-MM-DD' in local tz so the calendar shows the picked day
  // (Date(iso) parses as UTC and can drift a day back in negative offsets).
  const dateValue = iso
    ? (() => {
        const [y, m, d] = iso.split('-').map(Number);
        return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
          ? new Date(y, m - 1, d)
          : null;
      })()
    : null;
  return (
    // Wrapper width comes straight from the column size (minus a small inset
    // for cell padding). This forces the picker to its full intended width
    // regardless of whether content fills the cell, so X-button appearance
    // doesn't visually grow the picker. The [&_div]:!min-w-0 + [&_input]:!min-w-0
    // override neutralizes the picker's internal min-w-[100px] and the input's
    // intrinsic content min-width so internal content can fit in this width.
    //
    // A staged override gets a ring + tooltip: the pick rewrites every appt
    // date in the dispatch group on dispatch, so "I changed this" has to be
    // visible at a glance across a drag-filled range before the user commits.
    <div
      className={`overflow-hidden rounded ${isOverridden ? 'ring-2 ring-amber-500' : ''}`}
      style={{ width: Math.max(width - 8, 0) }}
      title={isOverridden ? 'Date changed — applies on dispatch to every leg in this dispatch group past the delivery leg' : undefined}
    >
    <DateTimePicker
      mode="date"
      selectionType="single"
      inputVariant="compact"
      labelType="none"
      autoApply
      usePortal
      autoFlip
      className="w-full [&_div]:!min-w-0 [&_input]:!min-w-0"
      value={dateValue}
      onChange={(val) => {
        const d = val instanceof Date ? val : null;
        setPickedApptDates(prev => {
          if (!d) {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          }
          const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return { ...prev, [key]: next };
        });
      }}
    />
    </div>
  );
};

// Extended row type for multileg support with sub-rows
interface DispatchPlannerRow extends Order {
  // Unique ID for TanTable row identification
  id: string;
  // Sub-row indicator
  isSubRow?: boolean;
  // Leg-specific data for sub-rows
  legOriginName?: string;
  // Full origin location name (lname) for the sub-row; legOriginName shows the
  // alias when set. Carried so the hover tooltip + global search see the full name.
  legOriginLname?: string;
  legOriginCityState?: string;
  legDestName?: string;
  legDestLname?: string;
  legDestCityState?: string;
  legDrop?: string;
  // Computed filterable values
  origType?: string;
  destType?: string;
  requirements?: string[];
  // Sub-rows for connected moves (parent rows only)
  subLegs?: DispatchPlannerRow[];
}

/**
 * PRO# / leg cell for the planner grids. Reads the per-page viewed-pros set
 * so an inspected pro stays highlighted (light green) across every grid —
 * matching the ProNumberLink / NotSentToGwiMoveCard pattern. The cell reads
 * the hook itself so only it re-renders when a pro is marked viewed (no
 * column-def recompute). Marking viewed happens at the open chokepoint
 * (openOrderModal) for the main link, and inline here for the legacy-CMS
 * shortcut since that path doesn't open the drawer.
 */
const ProLegCell: React.FC<{
  pronumber: number;
  legnum?: number;
  onOpen: () => void;
  legacyCmsUrl: string;
}> = ({ pronumber, legnum, onOpen, legacyCmsUrl }) => {
  // Viewed/last-viewed highlight + click-to-mark is handled by TanTable's
  // trackViewedColumnId="proLeg" wrapper (see the TanTable instances below).
  return (
    <div className="flex items-center gap-1.5 pl-2">
      <button
        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer text-left transition-colors duration-150"
        onClick={onOpen}
      >
        {pronumber}-{legnum || 0}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.open(`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`, '_blank', 'noopener,noreferrer');
        }}
        className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
        title="Open in legacy CMS"
      >
        <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
      </button>
    </div>
  );
};

// Restored from the old Dispatch Planner: a row of leg-type quick-filter
// toggles (Pick Up / Delivery / Terminate / Other) that sit next to a card's
// search box (via TanTable's `searchPrefix`). Active buttons turn green and
// OR-filter the card to orders having any selected leg type.
const LEG_TYPE_BUTTONS: { key: string; label: string }[] = [
  { key: 'pickup', label: 'Pick Up' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'terminate', label: 'Terminate' },
  { key: 'other', label: 'Other' },
];

const LegTypeFilters: React.FC<{
  active: string[];
  onToggle: (key: string) => void;
}> = ({ active, onToggle }) => (
  <div className="flex items-center gap-1">
    {LEG_TYPE_BUTTONS.map(({ key, label }) => (
      <button
        key={key}
        type="button"
        onClick={() => onToggle(key)}
        aria-pressed={active.includes(key)}
        className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
          active.includes(key)
            ? 'bg-green-600 text-white'
            : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

function DispatchPlannerContent() {
  // Get current user from MSAL
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  // Permissions provide the user's v1 (CMS) username, used as the dispatcher key
  const { permissions } = usePermissions();

  // Per-page "already inspected" tracking — highlights a pro in the grids
  // once it's been opened (see ProLegCell). Provided by ViewedProsProvider.
  const { markViewed } = useViewedPros();

  // Global filters (shared across pages)
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers: selectedDispatchers,
    setSelectedOrderDispatchers: setSelectedDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters (page-specific, persisted via user settings)
  const [selectedOTRs, setSelectedOTRs] = useState<string[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>([]);
  const [selectedLive, setSelectedLive] = useState<string[]>([]);
  const [selectedSize, setSelectedSize] = useState<string[]>([]);
  const [selectedContainerType, setSelectedContainerType] = useState<string[]>([]);
  const [selectedChassisType, setSelectedChassisType] = useState<string[]>([]);
  const localFiltersLoaded = useRef(false);
  // State twin of the ref: gates the data fetch so boot waits for the saved
  // local filters instead of fetching with defaults and refetching on restore.
  const [localFiltersReady, setLocalFiltersReady] = useState(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('dispatch_planner_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedOTRs) setSelectedOTRs(filters.selectedOTRs);
          if (filters.selectedCustomers) setSelectedCustomers(filters.selectedCustomers);
          if (filters.selectedRequirements) setSelectedRequirements(filters.selectedRequirements);
          if (filters.selectedLive) setSelectedLive(filters.selectedLive);
          if (filters.selectedSize) setSelectedSize(filters.selectedSize);
          if (filters.selectedContainerType) setSelectedContainerType(filters.selectedContainerType);
          if (filters.selectedChassisType) setSelectedChassisType(filters.selectedChassisType);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
      // Batched with the setters above (same tick), so the gated fetch effect
      // runs exactly once with the restored values.
      setLocalFiltersReady(true);
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters when they change (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!localFiltersLoaded.current) return;
    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('dispatch_planner_filters', {
          selectedOTRs,
          selectedCustomers,
          selectedRequirements,
          selectedLive,
          selectedSize,
          selectedContainerType,
          selectedChassisType,
        });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);
    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedOTRs, selectedCustomers, selectedRequirements, selectedLive, selectedSize, selectedContainerType, selectedChassisType]);

  // Filter dropdown options
  const otrOptions: MultiSelectOption[] = [
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' }
  ];
  const liveOptions: MultiSelectOption[] = [
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' }
  ];
  const requirementOptions: MultiSelectOption[] = [
    { value: 'hot', label: 'HOT Load', icon: <span className="gw-icon gw-icon-hotjar text-brand-red" style={{ fontSize: '16px' }}></span> },
    { value: 'hazmat', label: 'Hazmat', icon: <span className="flaticon flaticon-warning text-orange-500" style={{ fontSize: '16px' }}></span> },
    { value: 'overweight', label: 'Overweight', icon: <span className="flaticon flaticon-weight text-heading" style={{ fontSize: '16px' }}></span> },
    { value: 'early', label: 'Early', icon: <span className="gw-icon gw-icon-early text-heading" style={{ fontSize: '16px' }}></span> },
    { value: 'triaxle', label: 'Triaxle', icon: <span className="gw-icon gw-icon-dual-axle text-blue-600" style={{ fontSize: '16px' }}></span> },
    { value: 'swing', label: 'Swing', icon: <span className="flaticon flaticon-cargo text-purple-600" style={{ fontSize: '18px' }}></span> },
    { value: 'tank', label: 'Tank', icon: <span className="flaticon flaticon-oil-tank text-purple-600" style={{ fontSize: '18px' }}></span> },
    { value: 'ppe', label: 'PPE Required', icon: <ShieldCheckIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" /> },
  ];
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  // Admin-published default layouts for the planner tables, keyed by settingsKey.
  const [plannerDefaults, setPlannerDefaults] = useState<Record<string, any>>({});
  useEffect(() => {
    userSettingsService.getDefaultSettingsByPrefix('dispatch-planner')
      .then(setPlannerDefaults)
      .catch(() => { /* best-effort: no defaults = code defaults apply */ });
  }, []);
  // TanTable composes the full default blob; we just persist + cache it.
  const handleSaveDefaults = useCallback(async (settingsKey: string, stored: any) => {
    await userSettingsService.saveDefaultSetting(settingsKey, stored);
    setPlannerDefaults(prev => ({ ...prev, [settingsKey]: stored }));
  }, []);
  const [customerOptions, setCustomerOptions] = useState<MultiSelectOption[]>([]);
  const [sizeOptions, setSizeOptions] = useState<MultiSelectOption[]>([]);
  const [containerTypeOptions, setContainerTypeOptions] = useState<MultiSelectOption[]>([]);
  const [chassisTypeOptions, setChassisTypeOptions] = useState<MultiSelectOption[]>([]);

  // Fetch filter dropdown data on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [regionsRes, filterOptionsRes] = await Promise.all([
          ordersService.getRegions(),
          ordersService.getFilterOptions()
        ]);
        setRegionOptions(regionsRes.data.regions.map((region: string) => ({ value: region, label: region })));
        setSizeOptions(filterOptionsRes.sizes.map((s: string) => ({ value: s, label: s })));
        setContainerTypeOptions(filterOptionsRes.containerTypes.map((t: string) => ({ value: t, label: t })));
        setChassisTypeOptions(filterOptionsRes.chassisTypes.map((t: string) => ({ value: t, label: t })));
      } catch (err) {
        // Silently fail
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch customers - refetch when dispatcher filter changes
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const dispatcher = selectedDispatchers.length > 0 ? selectedDispatchers[0] : undefined;
        const customersRes = await ordersService.getCustomers(dispatcher);
        setCustomerOptions(customersRes.data.map((c: any) => ({ value: c.recno.toString(), label: c.name })));
      } catch (err) {
        // Silently fail
      }
    };
    fetchCustomers();
    if (selectedDispatchers.length === 0) {
      setSelectedCustomers([]);
    }
  }, [selectedDispatchers]);

  // State for imports data
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTabs, setActiveTabs] = useState<string[]>([]);
  const [cardHeight, setCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');

  // State for the Domestic card (DOMESTIC / INBOUND / OUTBOUND). The rows come
  // from the same /orders/imports fetch as the Imports card — see the
  // partition below — so only the per-card UI state lives here.
  const [activeDomesticTabs, setActiveDomesticTabs] = useState<string[]>([]);
  const [domesticCardHeight, setDomesticCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');

  // State for exports data
  const [exports, setExports] = useState<Order[]>([]);
  const [exportsLoading, setExportsLoading] = useState(false);
  const [activeExportTabs, setActiveExportTabs] = useState<string[]>([]);
  const [exportsCardHeight, setExportsCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');

  // Leg-type quick-filter buttons (Pick Up / Delivery / Terminate / Other),
  // independent per card — restored from the old Dispatch Planner toolbar.
  // Session-only state: resets on reload, like the other quick-filters.
  const [legTypeFiltersImports, setLegTypeFiltersImports] = useState<string[]>([]);
  const [legTypeFiltersDomestic, setLegTypeFiltersDomestic] = useState<string[]>([]);
  const [legTypeFiltersExports, setLegTypeFiltersExports] = useState<string[]>([]);

  // State for drop released data
  const [dropReleased, setDropReleased] = useState<Order[]>([]);
  const [dropReleasedLoading, setDropReleasedLoading] = useState(false);
  const [dropReleasedCardHeight, setDropReleasedCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');

  // State for Not Sent To gwiMove data
  const [notSentData, setNotSentData] = useState<NotSentToGwiMoveOrder[]>([]);
  const [notSentTotalCount, setNotSentTotalCount] = useState(0);
  const [notSentLoading, setNotSentLoading] = useState(false);
  const [notSentError, setNotSentError] = useState<string | null>(null);
  const [notSentCardHeight, setNotSentCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');

  // GridStack — each card has its own lock toggle (matches Dispatch Dashboard pattern).
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const isGridInitializing = useRef(false);

  type GridPos = { x: number; y: number; w: number; h: number };
  type CardKey = 'imports' | 'domestic' | 'exports' | 'dropReleased' | 'dropNotDispatched' | 'notSent';
  const GRID_LAYOUT_KEY = 'dispatch-planner-layout';
  const DEFAULT_LAYOUT: Record<CardKey, GridPos> = {
    imports:      { x: 0, y: 0,  w: 6, h: 6 },
    exports:      { x: 6, y: 0,  w: 6, h: 6 },
    domestic:     { x: 0, y: 6,  w: 6, h: 6 },
    notSent:      { x: 6, y: 6,  w: 6, h: 6 },
    dropReleased: { x: 0, y: 12, w: 6, h: 6 },
    dropNotDispatched: { x: 6, y: 12, w: 6, h: 6 },
  };
  const DEFAULT_LOCKED: Record<CardKey, boolean> = {
    imports: true,
    domestic: true,
    exports: true,
    dropReleased: true,
    dropNotDispatched: true,
    notSent: true,
  };

  const [layout, setLayout] = useState<Record<string, GridPos>>(() => {
    try {
      const saved = localStorage.getItem(GRID_LAYOUT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as { layout?: Record<string, GridPos> } & Record<string, GridPos>;
        // Support both new shape ({ layout, locked }) and legacy shape (positions at top level).
        const positions = parsed.layout ?? parsed;
        // Heal any card an older build persisted at the collapsed height (h<2),
        // which would otherwise render header-only after this reload. A card
        // the saved layout predates (e.g. Domestic) is appended below the
        // saved cards rather than dropped onto its default slot.
        return healLayoutHeights(placeMissingCards(positions, DEFAULT_LAYOUT), DEFAULT_LAYOUT);
      }
    } catch {
      // ignore parse errors, fall through to defaults
    }
    return DEFAULT_LAYOUT;
  });

  const [cardLocked, setCardLocked] = useState<Record<CardKey, boolean>>(() => {
    try {
      const saved = localStorage.getItem(GRID_LAYOUT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as { locked?: Record<CardKey, boolean> };
        if (parsed.locked) return { ...DEFAULT_LOCKED, ...parsed.locked };
      }
    } catch {
      // ignore parse errors, fall through to defaults
    }
    return DEFAULT_LOCKED;
  });

  // Collapsed/expanded state per card, restored from the saved layout so it
  // survives a reload (matches the position/lock persistence above).
  const initialCollapsed = useMemo<Record<CardKey, boolean>>(() => {
    const defaults: Record<CardKey, boolean> = {
      imports: false, domestic: false, exports: false, dropReleased: false, dropNotDispatched: false, notSent: false,
    };
    try {
      const saved = localStorage.getItem(GRID_LAYOUT_KEY);
      if (saved) return readSavedCollapsed(JSON.parse(saved), defaults);
    } catch {
      // ignore parse errors, fall through to defaults
    }
    return defaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [importsCollapsed, setImportsCollapsed] = useState(initialCollapsed.imports);
  const [domesticCollapsed, setDomesticCollapsed] = useState(initialCollapsed.domestic);
  const [exportsCollapsed, setExportsCollapsed] = useState(initialCollapsed.exports);
  const [dropReleasedCollapsed, setDropReleasedCollapsed] = useState(initialCollapsed.dropReleased);
  const [dropNotDispatchedCollapsed, setDropNotDispatchedCollapsed] = useState(initialCollapsed.dropNotDispatched);
  const [dropNotDispatchedCardHeight, setDropNotDispatchedCardHeight] = useState<'sm' | 'md' | 'lg'>('lg');
  const [notSentCollapsed, setNotSentCollapsed] = useState(initialCollapsed.notSent);

  // Keep latest lock state addressable from the GridStack init effect (which runs once).
  const cardLockedRef = useRef(cardLocked);
  useEffect(() => { cardLockedRef.current = cardLocked; }, [cardLocked]);

  // Latest layout and collapsed map, addressable from the once-only init effect
  // and the collapse handler (which both run outside React's render closure).
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const collapsedRef = useRef<Record<CardKey, boolean>>(initialCollapsed);
  collapsedRef.current = {
    imports: importsCollapsed,
    domestic: domesticCollapsed,
    exports: exportsCollapsed,
    dropReleased: dropReleasedCollapsed,
    dropNotDispatched: dropNotDispatchedCollapsed,
    notSent: notSentCollapsed,
  };

  const persistLayoutAndLocks = useCallback((nextLayout: Record<string, GridPos>, nextLocked: Record<CardKey, boolean>) => {
    try {
      localStorage.setItem(
        GRID_LAYOUT_KEY,
        JSON.stringify({ layout: nextLayout, locked: nextLocked, collapsed: collapsedRef.current }),
      );
    } catch {
      // ignore quota/serialization errors
    }
  }, []);

  // Initialize GridStack once on mount with drag/resize enabled at the grid level;
  // per-card lock state is applied via noMove/noResize on each widget below.
  useEffect(() => {
    if (!gridRef.current) return;

    isGridInitializing.current = true;
    const grid = GridStack.init({
      column: 12,
      cellHeight: 80,
      margin: 8,
      float: false,
      animate: true,
      disableDrag: false,
      disableResize: false,
      resizable: { handles: 'se' },
    }, gridRef.current);

    gridInstanceRef.current = grid;

    grid.on('change', () => {
      if (isGridInitializing.current) return;

      const next: Record<string, GridPos> = {};
      grid.getGridItems().forEach((el) => {
        const node = el.gridstackNode;
        const id = node?.id || el.getAttribute('gs-id');
        if (!id) return;
        const x = node?.x ?? parseInt(el.getAttribute('gs-x') || '0', 10);
        const y = node?.y ?? parseInt(el.getAttribute('gs-y') || '0', 10);
        const w = node?.w ?? parseInt(el.getAttribute('gs-w') || '6', 10);
        const h = node?.h ?? parseInt(el.getAttribute('gs-h') || '6', 10);
        if (w < 1 || h < 1) return;
        next[id] = { x, y, w, h };
      });

      setLayout((prev) => {
        // mergeGridLayout keeps the stored expanded height when a card reports
        // the collapsed h=1, so collapsing never corrupts the saved size.
        const merged = mergeGridLayout(prev, next);
        persistLayoutAndLocks(merged, cardLockedRef.current);
        return merged;
      });
    });

    // Apply initial lock state to each widget (locked cards can't be moved or resized).
    (Object.keys(cardLockedRef.current) as CardKey[]).forEach((key) => {
      if (!cardLockedRef.current[key]) return;
      const el = gridRef.current?.querySelector(`[gs-id="${key}"]`);
      if (el) grid.update(el as HTMLElement, { noMove: true, noResize: true });
    });

    // Apply saved collapsed state: remember each collapsed card's expanded
    // height (so a later expand restores it), then shrink it to header-only.
    (Object.keys(collapsedRef.current) as CardKey[]).forEach((key) => {
      if (!collapsedRef.current[key]) return;
      const el = gridRef.current?.querySelector(`[gs-id="${key}"]`) as HTMLElement | null;
      if (!el) return;
      const node = (el as any).gridstackNode;
      const currentH = node?.h || parseInt(el.getAttribute('gs-h') || '6', 10);
      expandedHeightsRef.current[key] = currentH;
      grid.update(el, { h: 1 });
    });

    setTimeout(() => {
      isGridInitializing.current = false;
    }, 200);

    return () => {
      grid.destroy(false);
      gridInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleCardLock = useCallback((key: CardKey, locked: boolean) => {
    setCardLocked((prev) => {
      const next = { ...prev, [key]: locked };
      persistLayoutAndLocks(layout, next);
      return next;
    });
    const grid = gridInstanceRef.current;
    if (grid && gridRef.current) {
      const el = gridRef.current.querySelector(`[gs-id="${key}"]`);
      if (el) grid.update(el as HTMLElement, { noMove: locked, noResize: locked });
    }
  }, [layout, persistLayoutAndLocks]);

  // Pre-collapse heights so other cards can compact upward — same pattern as Dispatch Dashboard.
  const expandedHeightsRef = useRef<Partial<Record<CardKey, number>>>({});
  const handleToggleCollapse = useCallback((key: CardKey, isCollapsed: boolean, setCollapsed: React.Dispatch<React.SetStateAction<boolean>>) => {
    const grid = gridInstanceRef.current;
    const el = gridRef.current?.querySelector(`[gs-id="${key}"]`) as HTMLElement | null;
    if (grid && el) {
      if (!isCollapsed) {
        // Collapsing: remember current height, shrink to header-only.
        const node = (el as any).gridstackNode;
        const currentH = node?.h || parseInt(el.getAttribute('gs-h') || '6', 10);
        expandedHeightsRef.current[key] = currentH;
        grid.update(el, { h: 1 });
      } else {
        // Expanding: restore prior height (or fall back to default).
        const restoreH = expandedHeightsRef.current[key] ?? DEFAULT_LAYOUT[key].h;
        grid.update(el, { h: restoreH });
        delete expandedHeightsRef.current[key];
      }
    }
    // Persist the new collapsed state so it survives a reload. Update the ref
    // synchronously so persistLayoutAndLocks writes the post-toggle value.
    collapsedRef.current = { ...collapsedRef.current, [key]: !isCollapsed };
    persistLayoutAndLocks(layoutRef.current, cardLockedRef.current);
    setCollapsed((prev) => !prev);
  }, [persistLayoutAndLocks]);

  // Real-time collaboration state
  const [activeDriverAssignments, setActiveDriverAssignments] = useState<{ [key: string]: ActiveUser }>({});
  const [activeDriverTyping, setActiveDriverTyping] = useState<{ [key: string]: string }>({});

  // Order details drawer state — ProDetailSlideOver fetches/refreshes the
  // order itself, so the page only needs to track the selected pronumber.
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const openOrderModal = useCallback((order: Order) => {
    markViewed(order.pronumber);
    setSelectedPro(order.pronumber);
  }, [markViewed]);

  const closeOrderModal = useCallback(() => {
    setSelectedPro(null);
  }, []);

  // Open order modal by pronumber only (for NotSentToGwiMoveCard)
  const openOrderModalByPronumber = useCallback((pronumber: number) => {
    markViewed(pronumber);
    setSelectedPro(pronumber);
  }, [markViewed]);

  // One shared Open TT column def for all three grids — stable identity, so
  // adding it to the columns memos below can't trigger a rebuild (which would
  // cost driver-cell focus).
  const openTtColumn = useMemo(
    () => buildOpenTtColumn<DispatchPlannerRow>(openOrderModalByPronumber),
    [openOrderModalByPronumber],
  );

  // Dispatch state
  const [dispatchStatuses, setDispatchStatuses] = useState<Record<string, 'success' | 'error'>>({});
  const [isDispatching, setIsDispatching] = useState(false);
  const [clearResetKey, setClearResetKey] = useState(0);

  // "Send to gwiMove" toggle for this dispatch batch. Initial value comes
  // from the user's defaultSendToGwiMove preference; the footer toggle lets
  // them override it for this batch without changing the global setting.
  // Passed through to each legData entry in the dispatch payload.
  const { defaultSendToGwiMove } = useWorkingStatusPreference();
  const [sendToGwiMove, setSendToGwiMove] = useState(defaultSendToGwiMove);
  // Resync if the user changes the global setting in another tab / panel.
  useEffect(() => { setSendToGwiMove(defaultSendToGwiMove); }, [defaultSendToGwiMove]);

  // Track which PROs have valid driver assignments — split by table for footer display
  // Keyed by `${pronumber}-${primary legnum}` — the same pro can map to multiple
  // orders after the dispatch-group split, so pronumber alone would conflate them
  // (validating one order would mark its sibling order valid too, and the pending
  // counter would be off by one).
  const [validImportPros, setValidImportPros] = useState<Set<string>>(new Set());
  const [validExportPros, setValidExportPros] = useState<Set<string>>(new Set());
  const [validDropReleasePros, setValidDropReleasePros] = useState<Set<string>>(new Set());

  // Keys (`${pronumber}-${primary legnum}`) of driver assignments THIS user made
  // in this session — typed, drag-filled, or picked from the suggest popup. The
  // footer count, "Dispatch All", and "Clear All" are all gated on this set so
  // they only ever act on the current user's own work.
  //
  // This is the fix for coworker assignments leaking in: a driver pushed over
  // socket.io (handleLiveUpdate → applyDriverAssignment) changes a cell's driver
  // prop, which makes the cell re-validate and fire onValidationChange — adding
  // the coworker's pro to validImportPros. But socket-driven assignments never
  // pass through the handle*DriverAssigned chokepoints below, so they never land
  // here, and intersecting against this set excludes them.
  const [locallyEditedKeys, setLocallyEditedKeys] = useState<Set<string>>(new Set());
  // Which board the current user assigned each locally-edited key from. A move
  // can live in more than one board's dataset (e.g. an import that's also drop
  // released); applyDriverAssignment fans the driver into all three arrays, so
  // every board's cell for that pro re-validates and reports it — which would
  // count one move under multiple footer buckets. Bucketing the count by the
  // board the user actually acted in keeps each move counted exactly once.
  const assignedBoardRef = useRef<Record<string, CardKey>>({});
  // Mark/unmark a (pro, primary legnum) as edited by the current user, tagging
  // the board it was assigned from. Empty driver (a clear) removes it.
  const markLocallyEdited = useCallback((pronumber: number, legnum: number, driverUnit: string, board: CardKey) => {
    const key = `${pronumber}-${legnum}`;
    const isAssign = !!driverUnit && driverUnit.trim() !== '';
    if (isAssign) assignedBoardRef.current[key] = board;
    else delete assignedBoardRef.current[key];
    setLocallyEditedKeys(prev => {
      if (isAssign === prev.has(key)) return prev; // no-op, keep identity stable
      const next = new Set(prev);
      if (isAssign) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  const { setDispatchButton } = useDispatchFooter();

  // Drag-to-fill state
  const [dragFillActive, setDragFillActive] = useState(false);
  const [dragFillSourceValue, setDragFillSourceValue] = useState('');
  const [dragFillSourceKey, setDragFillSourceKey] = useState('');
  const [dragFillSourceY, setDragFillSourceY] = useState(0);
  const [dragFillTargets, setDragFillTargets] = useState<Set<string>>(new Set());

  // Pending drag-fill held back by the batched warning dialog. Stashes the
  // resolved (pronumber, legnum, tableType) targets + the source value so the
  // fill can resume on confirm without re-parsing.
  const [pendingDragFill, setPendingDragFill] = useState<{
    targets: { pronumber: number; legnum: number; tableType: string }[];
    sourceValue: string;
    warnedPros: WarnedPro[];
  } | null>(null);

  // User-picked appointment dates for drop-released rows whose order-level
  // appointmentDate is empty. Keyed by `${pronumber}-${primary legnum}`.
  // When set, this date is sent on dispatch in place of today's-date default.
  const [pickedApptDates, setPickedApptDates] = useState<Record<string, string>>({});

  // Real-time collaboration for Dispatch Planner
  const handleLiveUpdate = useCallback((update: any) => {
    console.log('[DispatchPlanner] Received live update:', update.update_type, update.payload);

    // Handle real-time updates from other users
    if (update.update_type === 'driver_assignment_started') {
      const { pronumber, legnum, user } = update.payload;
      const key = `${pronumber}-${legnum}`;
      console.log('[DispatchPlanner] Setting active assignment:', key, user);
      setActiveDriverAssignments((prev) => ({
        ...prev,
        [key]: user,
      }));
    } else if (update.update_type === 'driver_assignment_stopped') {
      const { pronumber, legnum } = update.payload;
      const key = `${pronumber}-${legnum}`;
      console.log('[DispatchPlanner] Clearing active assignment:', key);
      setActiveDriverAssignments((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      // Clear typing value when user stops editing (but the driver value will remain via driver_assigned event)
      setActiveDriverTyping((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } else if (update.update_type === 'driver_typing') {
      const { pronumber, legnum, value } = update.payload;
      const key = `${pronumber}-${legnum}`;
      console.log('[DispatchPlanner] Setting typing value:', key, value);
      setActiveDriverTyping((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (update.update_type === 'driver_assigned') {
      const { pronumber, legnum, driver } = update.payload;
      console.log('[DispatchPlanner] Received driver assignment from another user:', { pronumber, legnum, driver });

      // Fan out to all three arrays — the same leg may live in multiple tables
      // (e.g., a Drop Released leg also showing in Imports/Exports).
      setOrders(prev => applyDriverAssignment(prev, pronumber, legnum, driver));
      setExports(prev => applyDriverAssignment(prev, pronumber, legnum, driver));
      setDropReleased(prev => applyDriverAssignment(prev, pronumber, legnum, driver));
    }
  }, []);

  const { broadcastUpdate, isConnected, socket, activeUsers } = useRealtimeCollaboration({
    pageId: 'dispatch-planner',
    userName: currentUser?.name,
    userId: currentUser?.localAccountId || currentUser?.homeAccountId,
    userEmail: currentUser?.username,
    onLiveUpdate: handleLiveUpdate,
  });

  // Helper to format dates as MM/DD
  const formatDateShort = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';

    try {
      // Get date part (in case of datetime with time)
      const datePart = dateStr.split(' ')[0];

      // Check if it's in YYYY-MM-DD format
      if (datePart.includes('-')) {
        const parts = datePart.split('-');
        if (parts.length === 3) {
          const [, month, day] = parts;
          // Make sure month and day are valid
          if (month && day && !isNaN(Number(month)) && !isNaN(Number(day)) && Number(month) > 0 && Number(day) > 0) {
            return `${month}/${day}`;
          }
        }
      }

      // Check if it's in MM/DD/YYYY format
      if (datePart.includes('/')) {
        const parts = datePart.split('/');
        if (parts.length >= 2) {
          const [month, day] = parts;
          // Make sure month and day are valid
          if (month && day && !isNaN(Number(month)) && !isNaN(Number(day)) && Number(month) > 0 && Number(day) > 0) {
            return `${month}/${day}`;
          }
        }
      }
    } catch (error) {
      console.error('Error formatting date:', dateStr, error);
    }

    // If we can't parse it, return empty string
    return '';
  };

  // Refs for collapsed state — used in fetchAllData to skip collapsed tables on background refresh
  const importsCollapsedRef = useRef(importsCollapsed);
  importsCollapsedRef.current = importsCollapsed;
  const domesticCollapsedRef = useRef(domesticCollapsed);
  domesticCollapsedRef.current = domesticCollapsed;
  const exportsCollapsedRef = useRef(exportsCollapsed);
  exportsCollapsedRef.current = exportsCollapsed;
  const dropReleasedCollapsedRef = useRef(dropReleasedCollapsed);
  dropReleasedCollapsedRef.current = dropReleasedCollapsed && dropNotDispatchedCollapsed;

  // AbortController ref for batch fetches — silent refreshes skip if in-flight,
  // user-initiated fetches (filter changes) abort the previous batch
  const fetchAbortControllerRef = useRef<AbortController | null>(null);

  // Merge new orders with existing state, preserving driver assignments and dispatch statuses
  // Track last driver edit — background refresh is paused for a cooldown period after any edit
  // to prevent polling from overwriting optimistic driver assignments (typed or drag-filled).
  const DRIVER_EDIT_COOLDOWN_MS = 10_000;
  const lastDriverEditRef = useRef<number>(0);

  const recordDriverEdit = () => {
    lastDriverEditRef.current = Date.now();

  };

  // Pending typed-but-uncommitted driver inputs, keyed by `${pronumber}-${legnum}`.
  // Cells write here on every keystroke / validation; mergeWithExisting overlays
  // these onto polling-refetched orders so the user's in-progress input survives
  // even if validation takes longer than the cooldown window.
  const PENDING_DRIVER_INPUT_TTL_MS = 30_000;
  const pendingDriverInputRef = useRef<Record<string, { value: string; timestamp: number }>>({});

  // Merge new orders with existing state, preserving driver assignments and dispatch statuses
  const mergeWithExisting = (prevOrders: Order[], newOrders: Order[]): Order[] => {
    // Drop stale pending driver inputs before merging
    const now = Date.now();
    for (const key of Object.keys(pendingDriverInputRef.current)) {
      if (now - pendingDriverInputRef.current[key].timestamp > PENDING_DRIVER_INPUT_TTL_MS) {
        delete pendingDriverInputRef.current[key];
      }
    }

    return newOrders.map(newOrder => {
      // Match by (pronumber, primary legnum) — appointmentDate can collide when
      // two dispatch groups for the same pro share a primary appt date.
      const existing = prevOrders.find(o => o.pronumber === newOrder.pronumber && (o.legnum || 0) === (newOrder.legnum || 0));
      const mergedLegs = newOrder.legs.map(newLeg => {
        const existingLeg = existing?.legs.find(l => l.legnum === newLeg.legnum);
        const refKey = `${newOrder.pronumber}-${newLeg.legnum || 0}`;
        const pending = pendingDriverInputRef.current[refKey];
        const committedDriver = (existingLeg && existingLeg.driver) || newLeg.driver;
        // Overlay the typed-but-uncommitted driver value when no driver is committed yet
        const driver = (!committedDriver || committedDriver === '') && pending ? pending.value : committedDriver;
        if (existingLeg && (existingLeg.driver || existingLeg.dispatchStatus) || pending) {
          return {
            ...newLeg,
            driver,
            dispatchStatus: existingLeg?.dispatchStatus || newLeg.dispatchStatus,
            errorReason: existingLeg?.errorReason || newLeg.errorReason,
          };
        }
        return newLeg;
      });
      if (existing) {
        return {
          ...newOrder,
          driver: existing.driver || newOrder.driver,
          legs: mergedLegs,
        };
      }
      return { ...newOrder, legs: mergedLegs };
    });
  };

  // Any orders-scope registry field can be added as a column on each of the three
  // boards. Separate selections per board — a user watching invoice state on Exports
  // has no reason to carry those columns on Imports — which is also what keeps each
  // board's poll independent: only the boards with a selection lose the shared
  // filterless cache.
  const importFields = useAdditionalFieldSelection('dispatch-planner');
  const domesticFields = useAdditionalFieldSelection('dispatch-planner-domestic');
  const exportFields = useAdditionalFieldSelection('dispatch-planner-exports');
  const dropReleasedFields = useAdditionalFieldSelection('dispatch-planner-dropreleased');
  const dropNotDispatchedFields = useAdditionalFieldSelection('dispatch-planner-drop-not-dispatched');
  // Imports and Domestic share one /orders/imports request, so it carries the
  // union of both cards' selections; each card still only renders its own.
  const importFieldsKey = useMemo(
    () => Array.from(new Set([...importFields.additionalFields, ...domesticFields.additionalFields])).sort().join(','),
    [importFields.additionalFields, domesticFields.additionalFields],
  );
  const exportFieldsKey = exportFields.additionalFields.join(',');
  const dropReleasedFieldsKey = [...new Set([...dropReleasedFields.additionalFields, ...dropNotDispatchedFields.additionalFields])].join(',');
  const plannerFieldsReady =
    importFields.ready && domesticFields.ready && exportFields.ready && dropReleasedFields.ready && dropNotDispatchedFields.ready;

  // Refetch imports with all filters applied
  const refetchImports = useCallback(async (showLoading = true, preserveDrivers = false, signal?: AbortSignal) => {
    if (showLoading) setLoading(true);
    try {
      // Backend accepts CSV multi-values via build_in_filter — one request per
      // endpoint instead of a cartesian product across selected filter values.
      const csv = (arr: string[]): string | undefined => (arr.length > 0 ? arr.join(',') : undefined);
      const response = await ordersService.getImports(
        csv(selectedDispatchers),
        csv(selectedCustomers),
        csv(selectedRegions),
        csv(selectedOTRs),
        csv(selectedLive),
        csv(selectedSize),
        csv(selectedContainerType),
        csv(selectedChassisType),
        { signal, additionalFields: importFieldsKey || undefined },
      );
      const allOrders = response.data;
      if (preserveDrivers) {
        setOrders(prev => mergeWithExisting(prev, allOrders));
      } else {
        setOrders(allOrders);
      }
    } catch (error: any) {
      // Aborted fetches are expected (filter changes / unmount) — don't log.
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch imports:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [selectedDispatchers, selectedRegions, selectedOTRs, selectedCustomers, selectedLive, selectedSize, selectedContainerType, selectedChassisType, importFieldsKey]);

  // Client-side requirements filtering
  const orderMatchesRequirements = useCallback((order: Order): boolean => {
    if (selectedRequirements.length === 0) return true;
    return selectedRequirements.every(req => {
      switch (req) {
        case 'hot': return order.hot === 'Y';
        case 'hazmat': return order.hazmat === 'Y';
        case 'overweight': return order.overweightPermitRequired === 'Y';
        case 'early': return order.isEarly;
        case 'triaxle': return order.chassisType?.toLowerCase().includes('triaxle');
        case 'swing': return order.equipType?.toLowerCase().includes('swing');
        case 'tank': return order.isTank === 'Y';
        case 'ppe': return order.requiresPpe === 'Y';
        default: return true;
      }
    });
  }, [selectedRequirements]);

  // `orders` is the whole /orders/imports response and stays the fan-out target
  // for driver assignment / dispatch. Only the display pipeline is split:
  // DOMESTIC / INBOUND / OUTBOUND rows render on the Domestic card.
  const { importOrders, domesticOrders } = useMemo(() => partitionPlannerImports(orders), [orders]);
  const filteredOrders = useMemo(
    () => importOrders.filter(o => orderMatchesRequirements(o) && orderMatchesLegTypes(o, legTypeFiltersImports)),
    [importOrders, orderMatchesRequirements, legTypeFiltersImports],
  );
  const filteredDomestic = useMemo(
    () => domesticOrders.filter(o => orderMatchesRequirements(o) && orderMatchesLegTypes(o, legTypeFiltersDomestic)),
    [domesticOrders, orderMatchesRequirements, legTypeFiltersDomestic],
  );
  const filteredExports = useMemo(
    () => exports.filter(o => orderMatchesRequirements(o) && orderMatchesLegTypes(o, legTypeFiltersExports)),
    [exports, orderMatchesRequirements, legTypeFiltersExports],
  );

  // Generate date tabs based on appointmentDate (always show static tabs).
  // Shared by the Imports and Domestic cards, each over its own rows.
  const buildDateTabs = (source: Order[]): TabData<Order>[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const tabs: TabData<Order>[] = [];

    // Check if there are past due orders (only if we have data)
    const hasPastDue = source.some(order => {
      if (!order.appointmentDate) return false;
      const orderDate = order.appointmentDate.split(' ')[0];
      return orderDate < todayStr;
    });

    // Add Past Due tab if there are past due orders
    if (hasPastDue) {
      tabs.push({
        id: 'past-due',
        label: 'Past Due',
        filterFn: (order) => {
          if (!order.appointmentDate) return false;
          const orderDate = order.appointmentDate.split(' ')[0];
          return orderDate < todayStr;
        }
      });
    }

    // Always generate tabs for next 14 days starting from today
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const label = i === 0 ? 'Today' : `${date.getMonth() + 1}/${date.getDate()}`;

      tabs.push({
        id: dateStr,
        label: label,
        filterFn: (order) => {
          if (!order.appointmentDate) return false;
          const orderDate = order.appointmentDate.split(' ')[0];
          return orderDate === dateStr;
        }
      });
    }

    return tabs;
  };
  const dateTabs = useMemo<TabData<Order>[]>(() => buildDateTabs(filteredOrders), [filteredOrders]);
  const domesticDateTabs = useMemo<TabData<Order>[]>(() => buildDateTabs(filteredDomestic), [filteredDomestic]);

  // Set initial active tab
  useEffect(() => {
    if (dateTabs.length > 0 && activeTabs.length === 0) {
      setActiveTabs([dateTabs[0].id]);
    }
  }, [dateTabs, activeTabs]);
  useEffect(() => {
    if (domesticDateTabs.length > 0 && activeDomesticTabs.length === 0) {
      setActiveDomesticTabs([domesticDateTabs[0].id]);
    }
  }, [domesticDateTabs, activeDomesticTabs]);

  // Refetch exports with all filters applied
  const refetchExports = useCallback(async (showLoading = true, preserveDrivers = false, signal?: AbortSignal) => {
    if (showLoading) setExportsLoading(true);
    try {
      const csv = (arr: string[]): string | undefined => (arr.length > 0 ? arr.join(',') : undefined);
      const response = await ordersService.getExports(
        csv(selectedDispatchers),
        csv(selectedCustomers),
        csv(selectedRegions),
        csv(selectedOTRs),
        csv(selectedLive),
        csv(selectedSize),
        csv(selectedContainerType),
        csv(selectedChassisType),
        { signal, additionalFields: exportFieldsKey || undefined },
      );
      const allExports = response.data;
      if (preserveDrivers) {
        setExports(prev => mergeWithExisting(prev, allExports));
      } else {
        setExports(allExports);
      }
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch exports:', error);
    } finally {
      if (showLoading) setExportsLoading(false);
    }
  }, [selectedDispatchers, selectedRegions, selectedOTRs, selectedCustomers, selectedLive, selectedSize, selectedContainerType, selectedChassisType, exportFieldsKey]);

  // Refetch drop released with all filters applied
  const refetchDropReleased = useCallback(async (showLoading = true, preserveDrivers = false, signal?: AbortSignal) => {
    if (showLoading) setDropReleasedLoading(true);
    try {
      const csv = (arr: string[]): string | undefined => (arr.length > 0 ? arr.join(',') : undefined);
      const fetchBoard = (notDispatched: boolean) => ordersService.getDropReleased(
        csv(selectedDispatchers),
        csv(selectedCustomers),
        csv(selectedRegions),
        undefined,
        csv(selectedOTRs),
        csv(selectedSize),
        csv(selectedContainerType),
        csv(selectedChassisType),
        { signal, additionalFields: dropReleasedFieldsKey || undefined, notDispatched },
      );
      const [ready, dashboard] = await Promise.all([fetchBoard(false), fetchBoard(true)]);
      const allDropReleased = mergeDropReleasedBoards(ready.data, dashboard.data);
      if (preserveDrivers) {
        setDropReleased(prev => mergeWithExisting(prev, allDropReleased));
      } else {
        setDropReleased(allDropReleased);
      }
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch drop released:', error);
    } finally {
      if (showLoading) setDropReleasedLoading(false);
    }
  }, [selectedDispatchers, selectedRegions, selectedOTRs, selectedCustomers, selectedSize, selectedContainerType, selectedChassisType, dropReleasedFieldsKey]);

  // Refetch Not Sent To gwiMove with dispatcher/region/OTR filters
  const refetchNotSentToGwiMove = useCallback(async (showLoading = true, signal?: AbortSignal) => {
    if (showLoading) setNotSentLoading(true);
    setNotSentError(null);
    try {
      const csv = (arr: string[]): string | undefined => (arr.length > 0 ? arr.join(',') : undefined);
      const response = await ordersService.getNotSentToGwiMove(
        csv(selectedDispatchers),
        csv(selectedCustomers),
        csv(selectedRegions),
        csv(selectedOTRs),
        { signal },
      );
      setNotSentData(response.data);
      setNotSentTotalCount(response.totalCount ?? response.count);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch not-sent-to-gwimove:', error);
      setNotSentError(error.message || 'Failed to fetch orders not sent to gwiMove');
    } finally {
      if (showLoading) setNotSentLoading(false);
    }
  }, [selectedDispatchers, selectedRegions, selectedCustomers, selectedOTRs]);

  // Fetch all data — silent refreshes skip if a fetch is already in-flight,
  // user-initiated fetches (filter changes) abort the previous batch and start fresh
  const fetchAllData = useCallback(async (silent = false) => {
    // Skip silent refresh while user is mid-drag or within cooldown after a driver edit
    const inCooldown = dragFillActiveRef.current || Date.now() - lastDriverEditRef.current < DRIVER_EDIT_COOLDOWN_MS;
    if (silent && inCooldown) {

      return;
    }

    if (fetchAbortControllerRef.current) {
      if (silent) {
        // Background refresh: skip if a fetch is already pending
        return;
      }
      // User-initiated: abort the previous batch
      fetchAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    fetchAbortControllerRef.current = abortController;

    try {
      // Always preserve drivers — merge on empty data is harmless, and this eliminates
      // the entire class of "non-preserving fetch wipes drivers" bugs from effect cascades.
      // handleClearAllDrivers explicitly resets when the user wants a clean slate.
      const preserve = true;
      const showLoading = !initialLoadComplete.current; // Only show loading spinners on first load

      const refetchPromises: Promise<void>[] = [];
      const signal = abortController.signal;
      // Skip collapsed tables on background refresh; always fetch on user-initiated (filter change)
      // One request feeds both Imports and Domestic — skip it only when both are collapsed.
      if (!silent || !importsCollapsedRef.current || !domesticCollapsedRef.current) refetchPromises.push(refetchImports(showLoading, preserve, signal));
      if (!silent || !exportsCollapsedRef.current) refetchPromises.push(refetchExports(showLoading, preserve, signal));
      if (!silent || !dropReleasedCollapsedRef.current) refetchPromises.push(refetchDropReleased(showLoading, preserve, signal));
      refetchPromises.push(refetchNotSentToGwiMove(showLoading, signal)); // NotSent manages its own collapse
      await Promise.all(refetchPromises);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Fetch error:', err);
      }
    } finally {
      // Only clear if this is still the active controller (not replaced by a newer fetch)
      if (fetchAbortControllerRef.current === abortController) {
        fetchAbortControllerRef.current = null;
      }
    }
  }, [refetchImports, refetchExports, refetchDropReleased, refetchNotSentToGwiMove]);

  // Fetch all data when filters change (user-initiated).
  // Ref is read synchronously inside fetchAllData (declared above); state drives the
  // background-refresh effect to re-run once the first load resolves.
  const initialLoadComplete = useRef(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    // Wait for BOTH filter restores (global + page) — the mount otherwise
    // fired the full 4-request batch up to 3x per load (2026-08-25 sweep).
    // The saved added-column selections are part of the requests, so they gate
    // the batch for the same reason: fetching before they resolve runs it twice.
    if (globalFiltersLoading || !localFiltersReady || !plannerFieldsReady) return;
    fetchAllData(false).then(() => {
      initialLoadComplete.current = true;
      setInitialLoadDone(true);
    });
  }, [fetchAllData, globalFiltersLoading, localFiltersReady, plannerFieldsReady]);

  // Background refresh every 5 seconds — but only while the tab is visible.
  // Parked planner tabs were polling all four /orders endpoints every 5s
  // around the clock (~38% of the family's 52k calls/day landed outside
  // business hours, 2026-08-26 access-log audit). The hook fires one
  // catch-up refresh the moment the tab is foregrounded again.
  useVisiblePolling(() => fetchAllData(true), 5000, initialLoadDone);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      fetchAbortControllerRef.current?.abort();
    };
  }, []);

  // Generate export date tabs (cutoff-based + Pool/Rolling)
  const exportDateTabs = useMemo<TabData<Order>[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const tabs: TabData<Order>[] = [];

    const hasPastDue = filteredExports.some(order => {
      if (!order.appointmentDate) return false;
      return order.appointmentDate.split(' ')[0] < todayStr;
    });

    if (hasPastDue) {
      tabs.push({
        id: 'past-due',
        label: 'Past Due',
        filterFn: (order) => {
          if (!order.appointmentDate) return false;
          return order.appointmentDate.split(' ')[0] < todayStr;
        }
      });
    }

    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const label = i === 0 ? 'Today' : `${date.getMonth() + 1}/${date.getDate()}`;
      tabs.push({
        id: `exp-${dateStr}`,
        label,
        filterFn: (order) => {
          if (!order.appointmentDate) return false;
          return order.appointmentDate.split(' ')[0] === dateStr;
        }
      });
    }

    // Pool and Rolling tabs
    if (filteredExports.some(o => o.cutoffDatetime === 'POOL')) {
      tabs.push({ id: 'pool', label: 'Pool', filterFn: (order) => order.cutoffDatetime === 'POOL' });
    }
    if (filteredExports.some(o => o.cutoffDatetime === 'ROLLING')) {
      tabs.push({ id: 'rolling', label: 'Rolling', filterFn: (order) => order.cutoffDatetime === 'ROLLING' });
    }

    return tabs;
  }, [filteredExports]);

  useEffect(() => {
    if (exportDateTabs.length > 0 && activeExportTabs.length === 0) {
      setActiveExportTabs([exportDateTabs[0].id]);
    }
  }, [exportDateTabs, activeExportTabs]);

  // Only include orders that match at least one tab (keeps total count consistent with tab sums)
  const tabCoveredOrders = useMemo(() => {
    return filteredOrders.filter(order => dateTabs.some(tab => tab.filterFn?.(order)));
  }, [filteredOrders, dateTabs]);

  const tabCoveredDomestic = useMemo(() => {
    return filteredDomestic.filter(order => domesticDateTabs.some(tab => tab.filterFn?.(order)));
  }, [filteredDomestic, domesticDateTabs]);

  const tabCoveredExports = useMemo(() => {
    return filteredExports.filter(order => exportDateTabs.some(tab => tab.filterFn?.(order)));
  }, [filteredExports, exportDateTabs]);

  // Compute filterable requirement labels from an order
  const getRequirements = (order: Order): string[] => {
    const reqs: string[] = [];
    if (order.hot === 'Y') reqs.push('HOT');
    if (order.isDaycab === 'Y') reqs.push('Daycab');
    if (order.hazmat === 'Y') reqs.push('Hazmat');
    if (order.isTank === 'Y') reqs.push('Tank');
    if (order.overweightPermitRequired === 'Y') reqs.push('Overweight');
    if (order.requiresPpe === 'Y') reqs.push('PPE');
    if (order.isEarly) reqs.push('Early');
    if (order.chassisType?.includes('93K')) reqs.push('93K Triaxle');
    else if (order.chassisType?.toLowerCase().includes('triaxle')) reqs.push('Triaxle');
    if (order.equipType?.toLowerCase().includes('swing')) reqs.push('Swing');
    return reqs;
  };

  // Transform exports for TanTable (same sub-row pattern as imports)
  const transformedExports = useMemo<DispatchPlannerRow[]>(() => {
    return tabCoveredExports.map(order => {
      const orderKey = `${order.pronumber}-${order.legnum || 0}`;
      const hasMultipleLegs = order.legs && order.legs.length > 1;
      const leg0 = order.legs?.[0];
      const parentRow: DispatchPlannerRow = {
        ...order,
        id: orderKey,
        isSubRow: false,
        origType: abbreviateLegType(leg0?.legtype),
        destType: abbreviateLegType(leg0?.legAction2),
        requirements: getRequirements(order),
      };
      if (hasMultipleLegs && order.legs) {
        parentRow.subLegs = order.legs.slice(1).map((leg) => ({
          ...order,
          id: `${orderKey}-leg${leg.legnum}`,
          isSubRow: true,
          legnum: leg.legnum,
          legOriginName: leg.origName,
          legOriginLname: leg.origLname,
          legOriginCityState: leg.origCityState,
          legDestName: leg.destName,
          legDestLname: leg.destLname,
          legDestCityState: leg.destCityState,
          legDrop: leg.legDrop,
          origType: abbreviateLegType(leg.legtype),
          destType: abbreviateLegType(leg.legAction2),
          subLegs: undefined,
        }));
      }
      return parentRow;
    });
  }, [tabCoveredExports]);

  // Drop released: requirements filter, date tabs, tab coverage, transform
  const filteredDropReleased = useMemo(() => dropReleased.filter(orderMatchesRequirements), [dropReleased, orderMatchesRequirements]);

  const transformedDropReleased = useMemo<DispatchPlannerRow[]>(() => {
    return filteredDropReleased.map(order => {
      const orderKey = `${order.pronumber}-${order.legnum || 0}`;
      const hasMultipleLegs = order.legs && order.legs.length > 1;
      const leg0 = order.legs?.[0];
      const parentRow: DispatchPlannerRow = {
        ...order,
        id: orderKey,
        isSubRow: false,
        origType: abbreviateLegType(leg0?.legtype),
        destType: abbreviateLegType(leg0?.legAction2),
        requirements: getRequirements(order),
      };
      if (hasMultipleLegs && order.legs) {
        parentRow.subLegs = order.legs.slice(1).map((leg) => ({
          ...order,
          id: `${orderKey}-leg${leg.legnum}`,
          isSubRow: true,
          legnum: leg.legnum,
          legOriginName: leg.origName,
          legOriginLname: leg.origLname,
          legOriginCityState: leg.origCityState,
          legDestName: leg.destName,
          legDestLname: leg.destLname,
          legDestCityState: leg.destCityState,
          legDrop: leg.legDrop,
          origType: abbreviateLegType(leg.legtype),
          destType: abbreviateLegType(leg.legAction2),
          subLegs: undefined,
        }));
      }
      return parentRow;
    });
  }, [filteredDropReleased]);

  const readyDropReleasedRows = useMemo(() => transformedDropReleased.filter(row => row.plannerDropReleased), [transformedDropReleased]);
  const notDispatchedDropReleasedRows = useMemo(() => transformedDropReleased.filter(row => row.dropReleasedNotDispatched), [transformedDropReleased]);

  // Handler for drop released driver assignment
  const handleDropReleasedDriverAssigned = useCallback((pronumber: number, legnum: number, driverUnit: string) => {
    recordDriverEdit();
    markLocallyEdited(pronumber, legnum, driverUnit, 'dropReleased');
    setDropReleased(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setOrders(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setExports(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    if (isConnected && broadcastUpdate) {
      broadcastUpdate('driver_assigned', { pronumber, legnum, driver: driverUnit });
    }
  }, [isConnected, broadcastUpdate]);

  // Handler for export driver assignment
  const handleExportDriverAssigned = useCallback((pronumber: number, legnum: number, driverUnit: string) => {
    recordDriverEdit();
    markLocallyEdited(pronumber, legnum, driverUnit, 'exports');
    setExports(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setOrders(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setDropReleased(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    if (isConnected && broadcastUpdate) {
      broadcastUpdate('driver_assigned', { pronumber, legnum, driver: driverUnit });
    }
  }, [isConnected, broadcastUpdate]);

  // Transform orders to rows with sub-rows for multileg moves. Shared by the
  // Imports and Domestic cards (same row shape, same columns).
  const toPlannerRows = useCallback((source: Order[]): DispatchPlannerRow[] => {
    return source.map(order => {
      const orderKey = `${order.pronumber}-${order.legnum || 0}`;
      const hasMultipleLegs = order.legs && order.legs.length > 1;
      const leg0 = order.legs?.[0];

      // Create the parent row
      const parentRow: DispatchPlannerRow = {
        ...order,
        id: orderKey,
        isSubRow: false,
        origType: abbreviateLegType(leg0?.legtype),
        destType: abbreviateLegType(leg0?.legAction2),
        requirements: getRequirements(order),
      };

      // If multiple legs, create sub-rows (skip first leg since it's shown on the parent)
      if (hasMultipleLegs && order.legs) {
        parentRow.subLegs = order.legs.slice(1).map((leg) => ({
          ...order,
          id: `${orderKey}-leg${leg.legnum}`,
          isSubRow: true,
          legnum: leg.legnum,
          legOriginName: leg.origName,
          legOriginLname: leg.origLname,
          legOriginCityState: leg.origCityState,
          legDestName: leg.destName,
          legDestLname: leg.destLname,
          legDestCityState: leg.destCityState,
          legDrop: leg.legDrop,
          origType: abbreviateLegType(leg.legtype),
          destType: abbreviateLegType(leg.legAction2),
          // Clear sub-rows on leg rows to prevent infinite nesting
          subLegs: undefined,
        }));
      }

      return parentRow;
    });
  }, []);
  const transformedOrders = useMemo<DispatchPlannerRow[]>(() => toPlannerRows(tabCoveredOrders), [tabCoveredOrders, toPlannerRows]);
  const transformedDomestic = useMemo<DispatchPlannerRow[]>(() => toPlannerRows(tabCoveredDomestic), [tabCoveredDomestic, toPlannerRows]);

  // Ref-based validation cache — survives cell remounts (virtualization, pagination, drag)
  const parentValidationRef = useRef<Record<string, 'valid' | 'invalid' | 'loading' | null>>({});
  const parentRejectionReasonsRef = useRef<Record<string, string[]>>({});

  // Handler for driver assignment - memoized to prevent columns re-render.
  // `board` tags which card the user acted in (footer count bucket); the
  // Imports and Domestic cards share `orders`, so the fan-out is identical.
  const handleBoardDriverAssigned = useCallback((board: 'imports' | 'domestic', pronumber: number, legnum: number, driverUnit: string) => {
    recordDriverEdit();
    markLocallyEdited(pronumber, legnum, driverUnit, board);

    // Fan out to all three arrays so a leg present in multiple tables stays in sync.
    // applyDriverAssignment is a no-op for arrays where the pro isn't present.
    setOrders(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setExports(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));
    setDropReleased(prev => applyDriverAssignment(prev, pronumber, legnum, driverUnit));

    // Broadcast driver assignment to other users
    if (isConnected && broadcastUpdate) {
      console.log('[DispatchPlanner] Broadcasting driver_assigned:', { pronumber, legnum, driverUnit });
      broadcastUpdate('driver_assigned', {
        pronumber,
        legnum,
        driver: driverUnit,
      });
    }
  }, [isConnected, broadcastUpdate]);
  const handleDriverAssigned = useCallback(
    (pronumber: number, legnum: number, driverUnit: string) => handleBoardDriverAssigned('imports', pronumber, legnum, driverUnit),
    [handleBoardDriverAssigned],
  );
  const handleDomesticDriverAssigned = useCallback(
    (pronumber: number, legnum: number, driverUnit: string) => handleBoardDriverAssigned('domestic', pronumber, legnum, driverUnit),
    [handleBoardDriverAssigned],
  );

  // Dispatch all valid driver assignments
  const handleDispatchAll = useCallback(async () => {
    setIsDispatching(true);

    // Keyed by `${pronumber}-${primary legnum}`. After the dispatch-group split,
    // the same pro can have multiple orders queued for dispatch — keying on
    // pronumber alone would cause one to overwrite the other in the map.
    const dispatchMap = new Map<string, { pronumber: number; legnum: number; source: string; legData: Array<{ legnum: number; origcode: number; destcode: number; driver: string; origapptdate?: string; destapptdate?: string }> }>();

    // Build a set of drop released pronumbers so we can fill in missing dates and tag source
    const dropReleasedPros = new Set(dropReleased.map(o => o.pronumber));
    const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    [...orders, ...exports, ...dropReleased].forEach(order => {
      const legWithDriver = order.legs.find(leg => leg.driver && leg.driver.trim() !== '');
      if (!legWithDriver) return;

      // Only dispatch assignments THIS user made. A driver fanned into local
      // state from a coworker's socket update will have a valid status here too,
      // but it won't be in locallyEditedKeys — so we skip it.
      if (!locallyEditedKeys.has(`${order.pronumber}-${order.legnum || 0}`)) return;

      const refKey = `${order.pronumber}-${legWithDriver.legnum}`;
      const status = parentValidationRef.current[refKey];
      if (status !== 'valid') return;

      const isDropReleased = dropReleasedPros.has(order.pronumber);

      const primaryLegnum = order.legnum || 0;
      // An explicit pick from the Drop Released Appt Date cell. Distinguished
      // from "untouched" by key presence, NOT by truthiness — an untouched row
      // must keep the existing per-side fill behavior, while a picked row
      // overrides every appt date in the dispatch group (see
      // resolveLegApptDates). Only Drop Released has the editable cell.
      const overrideIso = isDropReleased
        ? pickedApptDates[`${order.pronumber}-${primaryLegnum}`] ?? null
        : null;
      dispatchMap.set(`${order.pronumber}-${primaryLegnum}`, {
        pronumber: order.pronumber,
        legnum: primaryLegnum,
        source: isDropReleased ? 'dropreleased' : 'dispatch',
        legData: order.legs.map(leg => {
          const ld: any = {
            legnum: leg.legnum,
            origcode: leg.origcode,
            destcode: leg.destcode,
            driver: legWithDriver.driver!,
            // Per-batch toggle — backend uses this to call /sendtogwimove for
            // the dispatched legs after the CMS dispatch itself succeeds.
            sendToGwiMove,
          };

          // Both sides for every leg, per resolveLegApptDates' precedence:
          // an explicit pick overrides the whole group, otherwise an existing
          // date is echoed back and only blanks get filled.
          Object.assign(ld, resolveLegApptDates({
            leg,
            groupLegs: order.legs,
            isDropReleased,
            overrideIso,
            // A drop-released group can include the delivery leg (see
            // resolveLegApptDates) — its customer appointment is off limits.
            deliveryLeg: order.deliveryLeg,
            todayIso: todayStr,
          }));

          return ld;
        }),
      });
    });

    if (dispatchMap.size === 0) {
      alert('No valid drivers to dispatch.');
      setIsDispatching(false);
      return;
    }

    const dispatchOrders = Array.from(dispatchMap.values()).map(({ pronumber, legnum, source, legData }) => ({
      pronumber,
      legnum,  // primary legnum so the backend can echo it for disambiguation
      source,
      legData,
    }));

    try {
      const response = await api.post('/driver-dispatch/dispatch-drivers', dispatchOrders);
      const data = response.data;

      // Rule-emitted warnings, populated only by the native save path (cmsapi
      // surfaces its own). Without this they'd be dropped silently — e.g. the
      // "driver isn't a gwiMove driver" notice that v1 shows.
      const userMessages: { text?: string }[] = data.userMessages || [];
      if (userMessages.length > 0) {
        const lines = userMessages.map(m => m?.text).filter(Boolean);
        if (lines.length > 0) alert(lines.join('\n'));
      }

      const newStatuses: Record<string, 'success' | 'error'> = {};
      data.results.forEach((result: any) => {
        // Match on (pronumber, legnum) — two dispatch orders can share a pro.
        const dispatchOrder = dispatchOrders.find(
          d => d.pronumber === result.pronumber && d.legnum === result.legnum
        );
        if (dispatchOrder) {
          dispatchOrder.legData.forEach(ld => {
            newStatuses[`${result.pronumber}-${ld.legnum}`] = result.status;
          });
        }
      });

      setDispatchStatuses(newStatuses);

      // Update leg dispatch status for visual feedback
      const updateStatuses = (prev: Order[]) => prev.map(order => ({
        ...order,
        legs: order.legs.map(leg => {
          const key = `${order.pronumber}-${leg.legnum}`;
          if (newStatuses[key]) {
            return { ...leg, dispatchStatus: newStatuses[key], errorReason: undefined };
          }
          return leg;
        }),
      }));
      setOrders(updateStatuses);
      setExports(updateStatuses);
      setDropReleased(updateStatuses);

      // Remove successfully dispatched orders after 3 seconds
      setTimeout(() => {
        const filterSuccess = (prev: Order[]) => prev.filter(order => {
          const allSuccess = order.legs.every(leg => newStatuses[`${order.pronumber}-${leg.legnum}`] === 'success');
          return !allSuccess;
        });
        setOrders(filterSuccess);
        setExports(filterSuccess);
        setDropReleased(filterSuccess);
        setDispatchStatuses(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(key => { if (updated[key] === 'success') delete updated[key]; });
          return updated;
        });
        Object.keys(newStatuses).forEach(key => {
          if (newStatuses[key] === 'success') delete parentValidationRef.current[key];
        });
        // Remove successfully dispatched (pro, legnum) pairs from footer tracking
        const successKeys: string[] = data.results
          .filter((r: any) => r.status === 'success')
          .map((r: any) => `${r.pronumber}-${r.legnum}`);
        const removeSuccessFrom = (prev: Set<string>) => {
          const next = new Set(prev);
          successKeys.forEach(key => next.delete(key));
          return next;
        };
        setValidImportPros(removeSuccessFrom);
        setValidExportPros(removeSuccessFrom);
        setValidDropReleasePros(removeSuccessFrom);
        setLocallyEditedKeys(removeSuccessFrom);
        successKeys.forEach(key => delete assignedBoardRef.current[key]);
        setPickedApptDates(prev => {
          if (successKeys.length === 0) return prev;
          const next = { ...prev };
          successKeys.forEach(key => delete next[key]);
          return next;
        });
        setIsDispatching(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to dispatch drivers:', error);
      alert('Failed to dispatch drivers. Please try again.');
      setIsDispatching(false);
    }
  }, [orders, exports, dropReleased, pickedApptDates, sendToGwiMove, locallyEditedKeys]);

  // Clear all driver assignments
  // Bulk-edit handler for the Drop Released table's Appt Date column.
  // Fires when the user right-click-drags to select a range of rows and applies
  // a date via the modal.
  //
  // Writes a pick for EVERY selected parent row. It used to skip rows whose
  // legs all had appt dates, which meant a drag across 20 rows could land on
  // only a handful with no indication which were dropped. Dispatch now treats
  // a pick as an override, so there is nothing left to skip.
  const handleDropReleasedBulkEdit = useCallback((columnId: string, selectedRows: DispatchPlannerRow[], value: unknown) => {
    if (columnId !== 'appointmentDate') return;
    const date = typeof value === 'string' ? value : '';
    if (!date) return;
    setPickedApptDates(prev => {
      const next = { ...prev };
      for (const row of selectedRows) {
        // Sub-rows share their parent's order fields; the pick is keyed to the
        // dispatch group's primary leg, so writing one per sub-row would just
        // rewrite the same key.
        if (row.isSubRow) continue;
        next[`${row.pronumber}-${row.legnum || 0}`] = date;
      }
      return next;
    });
  }, []);

  const handleClearAllDrivers = useCallback(() => {
    // Only clear the current user's own assignments — a coworker's driver that
    // arrived over socket.io must stay on screen. Match on the primary-leg key
    // (the same key markLocallyEdited records).
    const editedKeys = locallyEditedKeys;
    const clearDrivers = (prev: Order[]) => prev.map(order => {
      if (!editedKeys.has(`${order.pronumber}-${order.legnum || 0}`)) return order;
      return {
        ...order,
        driver: '',
        legs: order.legs.map(leg => ({ ...leg, driver: '', dispatchStatus: undefined, errorReason: undefined })),
      };
    });
    setOrders(clearDrivers);
    setExports(clearDrivers);
    setDropReleased(clearDrivers);
    // dispatchStatuses only ever holds this user's own dispatch results, so a
    // full reset can't disturb coworker assignments.
    setDispatchStatuses({});
    // Drop validation/rejection cache only for the keys we just cleared,
    // leaving coworker entries intact.
    editedKeys.forEach(key => {
      delete parentValidationRef.current[key];
      delete parentRejectionReasonsRef.current[key];
      delete assignedBoardRef.current[key];
    });
    const removeEdited = (prev: Set<string>) => {
      const next = new Set(prev);
      editedKeys.forEach(key => next.delete(key));
      return next;
    };
    setValidImportPros(removeEdited);
    setValidExportPros(removeEdited);
    setValidDropReleasePros(removeEdited);
    setLocallyEditedKeys(new Set());
    setClearResetKey(k => k + 1);
  }, [locallyEditedKeys]);

  // Set dispatch footer buttons
  useEffect(() => {
    // Count only this user's own valid assignments, each under the board it was
    // assigned from. Two things to defend against:
    //   1. validXPros can pick up coworker pros (their socket-driven assignment
    //      makes the cell re-validate and fire onValidationChange) — excluded by
    //      iterating locallyEditedKeys, which only the assign chokepoints fill.
    //   2. A move shared across boards lands in multiple validXPros sets — so we
    //      bucket by assignedBoardRef (the board the user acted in), not by which
    //      set holds the key, to avoid counting one move twice.
    const ownerOf = assignedBoardRef.current;
    const isValidKey = (k: string) =>
      validImportPros.has(k) || validExportPros.has(k) || validDropReleasePros.has(k);
    let importCount = 0;
    let domesticCount = 0;
    let exportCount = 0;
    let dropReleaseCount = 0;
    locallyEditedKeys.forEach(k => {
      if (!isValidKey(k)) return;
      const board = ownerOf[k];
      if (board === 'exports') exportCount += 1;
      else if (board === 'dropReleased') dropReleaseCount += 1;
      else if (board === 'domestic') domesticCount += 1;
      else importCount += 1; // imports (or any untagged fallback)
    });
    const validCount = importCount + domesticCount + exportCount + dropReleaseCount;

    const pendingParts: string[] = [];
    if (importCount > 0) pendingParts.push(`${importCount} Import${importCount !== 1 ? 's' : ''}`);
    if (domesticCount > 0) pendingParts.push(`${domesticCount} Domestic`);
    if (exportCount > 0) pendingParts.push(`${exportCount} Export${exportCount !== 1 ? 's' : ''}`);
    if (dropReleaseCount > 0) pendingParts.push(`${dropReleaseCount} Drop Release${dropReleaseCount !== 1 ? 's' : ''}`);

    // Filter out current user and dedupe by user_id (same user with multiple tabs shows once)
    const currentUserId = activeUsers.find(user => user.sid === socket?.id)?.user_id;
    const seen = new Set<string>();
    const otherUsers = activeUsers.filter(user => {
      if (user.user_id === currentUserId || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
    const maxVisible = 20;
    const visibleUsers = otherUsers.slice(0, maxVisible);
    const overflowUsers = otherUsers.slice(maxVisible);

    const getInitials = (name: string): string => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getUserColor = (name: string): string => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      ];
      const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    setDispatchButton(
      <div className="flex items-center justify-between w-full">
        {/* Active Users Display — left side */}
        <div className="flex items-center gap-2">
          {otherUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Users className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <div className="flex -space-x-1">
                {visibleUsers.map(user => (
                  <Tooltip key={user.user_id} content={user.user_name}>
                    <div
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 text-[0.625rem] font-medium text-white cursor-pointer ${getUserColor(user.user_name)}`}
                    >
                      {getInitials(user.user_name)}
                    </div>
                  </Tooltip>
                ))}
                {overflowUsers.length > 0 && (
                  <Tooltip
                    content={
                      <div className="flex flex-col gap-1">
                        {overflowUsers.map(user => (
                          <span key={user.user_id}>{user.user_name}</span>
                        ))}
                      </div>
                    }
                  >
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 bg-gray-500 text-[0.625rem] font-medium text-white cursor-pointer">
                      +{overflowUsers.length}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pending count + Buttons — right side */}
        <div className="flex items-center gap-3">
          {validCount > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Pending Dispatch:{' '}
              <span className="font-medium text-heading">{pendingParts.join(' / ')}</span>
            </span>
          )}
          {/* Per-batch override of the user's defaultSendToGwiMove setting.
              When on, dispatched legs are also forwarded to /sendtogwimove. */}
          <label className="flex items-center gap-2 text-sm text-label cursor-pointer select-none">
            <Toggle
              checked={sendToGwiMove}
              onChange={setSendToGwiMove}
              size="sm"
              disabled={isDispatching}
            />
            Send to gwiMove
          </label>
          <button
            onClick={handleClearAllDrivers}
            disabled={isDispatching || locallyEditedKeys.size === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            Clear All Drivers
          </button>
          <button
            onClick={handleDispatchAll}
            disabled={isDispatching || validCount === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDispatching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Dispatching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Dispatch All Drivers
              </>
            )}
          </button>
        </div>
      </div>
    );

    return () => setDispatchButton(null);
  }, [validImportPros, validExportPros, validDropReleasePros, locallyEditedKeys, isDispatching, handleDispatchAll, handleClearAllDrivers, setDispatchButton, activeUsers, socket, sendToGwiMove]);

  // Pass dispatchStatus to DriverSelectorCell via column closure
  const dispatchStatusesRef = useRef(dispatchStatuses);
  dispatchStatusesRef.current = dispatchStatuses;

  // Refs for drag state — used in callbacks and column closures to avoid memo recomputation / cell remounts
  const dragFillActiveRef = useRef(dragFillActive);
  dragFillActiveRef.current = dragFillActive;
  const dragFillTargetsRef = useRef(dragFillTargets);
  dragFillTargetsRef.current = dragFillTargets;
  const dragFillSourceYRef = useRef(dragFillSourceY);
  dragFillSourceYRef.current = dragFillSourceY;

  // Drag-to-fill handlers
  // Track which table the drag started in so we query the right inputs
  const [dragFillTableType, setDragFillTableType] = useState('');

  const handleDragMouseDown = useCallback((key: string, value: string, y: number) => {
    // Extract table type from the key: "search-{tableType}-order-..."
    const tableMatch = key.match(/search-(\w+)-order-/);
    const tableType = tableMatch ? tableMatch[1] : 'imports';

    document.body.style.cursor = 'grabbing';
    setDragFillActive(true);
    setDragFillSourceValue(value);
    setDragFillSourceKey(key);
    setDragFillSourceY(y);
    setDragFillTargets(new Set([key]));
    setDragFillTableType(tableType);
  }, []);

  const dragFillTableTypeRef = useRef(dragFillTableType);
  dragFillTableTypeRef.current = dragFillTableType;

  // Stable refs for driver handlers — prevents handleDragMouseUp from being recreated
  // when handler identities change (due to broadcastUpdate/isConnected re-renders)
  const handleDriverAssignedRef = useRef(handleDriverAssigned);
  handleDriverAssignedRef.current = handleDriverAssigned;
  const handleDomesticDriverAssignedRef = useRef(handleDomesticDriverAssigned);
  handleDomesticDriverAssignedRef.current = handleDomesticDriverAssigned;
  const handleExportDriverAssignedRef = useRef(handleExportDriverAssigned);
  handleExportDriverAssignedRef.current = handleExportDriverAssigned;
  const handleDropReleasedDriverAssignedRef = useRef(handleDropReleasedDriverAssigned);
  handleDropReleasedDriverAssignedRef.current = handleDropReleasedDriverAssigned;

  const handleDragMouseOver = useCallback((_key: string, y: number) => {
    if (!dragFillActiveRef.current) return;

    // Query inputs matching the table type the drag started in
    const tableType = dragFillTableTypeRef.current;
    const allInputs = document.querySelectorAll(`input[data-driver-input="true"][data-table-type="${tableType}"]`);
    const inputsInRange: string[] = [];
    const minY = Math.min(dragFillSourceYRef.current, y);
    const maxY = Math.max(dragFillSourceYRef.current, y);

    allInputs.forEach((input) => {
      const inputRect = input.getBoundingClientRect();
      const inputY = inputRect.top + window.scrollY;
      if (inputY >= minY && inputY <= maxY) {
        const inputKey = input.getAttribute('data-key');
        if (inputKey) {
          inputsInRange.push(inputKey);
        }
      }
    });

    setDragFillTargets(new Set(inputsInRange));
  }, []);

  // Performs the actual assignment for an already-parsed target list. Shared
  // between the no-warnings fast path and the post-confirm resume path.
  const performDragFill = useCallback((
    targets: { pronumber: number; legnum: number; tableType: string }[],
    sourceValue: string,
  ) => {
    for (const t of targets) {
      if (t.tableType === 'imports') {
        handleDriverAssignedRef.current(t.pronumber, t.legnum, sourceValue);
      } else if (t.tableType === 'domestic') {
        handleDomesticDriverAssignedRef.current(t.pronumber, t.legnum, sourceValue);
      } else if (t.tableType === 'exports') {
        handleExportDriverAssignedRef.current(t.pronumber, t.legnum, sourceValue);
      } else if (t.tableType === 'dropreleased') {
        handleDropReleasedDriverAssignedRef.current(t.pronumber, t.legnum, sourceValue);
      }
    }
  }, []);

  const resetDragFillState = useCallback(() => {
    document.body.style.cursor = '';
    setDragFillActive(false);
    setDragFillSourceValue('');
    setDragFillSourceKey('');
    setDragFillSourceY(0);
    setDragFillTargets(new Set());
    setDragFillTableType('');
  }, []);

  const handleDragMouseUp = useCallback(() => {
    if (!dragFillActive) return;

    const targetsToFill = Array.from(dragFillTargets).filter(key => key !== dragFillSourceKey);

    // Parse target keys into structured form
    const parsed: { pronumber: number; legnum: number; tableType: string }[] = [];
    for (const key of targetsToFill) {
      const match = key.match(/search-(\w+)-order-(\d+)-(\d+)/);
      if (!match) continue;
      parsed.push({
        tableType: match[1],
        pronumber: parseInt(match[2], 10),
        legnum: parseInt(match[3], 10),
      });
    }

    // Resolve each target back to its Order so we can run warning checks.
    // Look in all three sources; the same PRO may exist in multiple lists.
    const allOrders: Order[] = [...orders, ...exports, ...dropReleased];
    const targetOrders: Order[] = [];
    const seen = new Set<number>();
    for (const t of parsed) {
      if (seen.has(t.pronumber)) continue;
      const o = allOrders.find(x => x.pronumber === t.pronumber);
      if (o) {
        targetOrders.push(o);
        seen.add(t.pronumber);
      }
    }

    const warnedPros = collectDragFillWarnings(targetOrders.map(o => ({
      pronumber: o.pronumber,
      isDrop: o.isDrop,
      deliveryLeg: o.deliveryLeg,
      dropReleaseDatetime: o.dropReleaseDatetime,
      moveType: o.moveType,
      firstRcvDate: o.firstRcvDate,
      legs: o.legs as any,
      warnings: o.warnings,
    })));

    if (warnedPros.length === 0) {
      performDragFill(parsed, dragFillSourceValue);
      resetDragFillState();
      return;
    }

    // Stash and wait for the user's answer. Reset drag UI state now so the
    // selection highlight clears; the actual fill happens on confirm.
    setPendingDragFill({ targets: parsed, sourceValue: dragFillSourceValue, warnedPros });
    resetDragFillState();
  }, [dragFillActive, dragFillTargets, dragFillSourceKey, dragFillSourceValue, orders, exports, dropReleased, performDragFill, resetDragFillState]);

  // Global mouseup listener for drag-to-fill
  useEffect(() => {
    if (dragFillActive) {
      document.addEventListener('mouseup', handleDragMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleDragMouseUp);
      };
    }
  }, [dragFillActive, handleDragMouseUp]);

  // Key account indicator color + label
  const getKeyAccountIndicator = (order: DispatchPlannerRow): { color: string; label: string | null } => {
    if (order.destIsNewLocation === 'Y') return { color: 'bg-blue-500', label: 'New Location' };
    switch (order.keyAccount?.toUpperCase()) {
      case 'KEY ACCOUNTS 1': return { color: 'bg-red-500', label: 'Key Account 1' };
      case 'KEY ACCOUNTS 2': return { color: 'bg-orange-500', label: 'Key Account 2' };
      case 'KEY ACCOUNTS 3': return { color: 'bg-yellow-400', label: 'Key Account 3' };
      case 'KEY ACCOUNTS 4': return { color: 'bg-green-500', label: 'Key Account 4' };
      case 'CONTRACT': return { color: 'bg-lime-500', label: 'Contract' };
      case 'KEY PROSPECT 1':
      case 'KEY PROSPECT 2':
      case 'KEY PROSPECT 3':
      case 'KEY PROSPECT 4': return { color: 'bg-purple-500', label: order.keyAccount };
      case 'HYPER FOCUS': return { color: 'bg-blue-500', label: 'Hyper Focus' };
      default: return { color: '', label: null };
    }
  };

  const legacyCmsUrl = config.LEGACY_CMS_URL;

  // Requirement icons renderer for Req column
  const renderRequirementIcons = (order: DispatchPlannerRow) => {
    return (
      <div className="flex gap-1 items-start">
        {order.hot === 'Y' && (
          <Tooltip content="HOT Load" placement="top">
            <span className="gw-icon gw-icon-hotjar text-brand-red cursor-help" style={{ fontSize: '16px' }}></span>
          </Tooltip>
        )}
        {order.isDaycab === 'Y' && (
          <Tooltip content="Daycab Required" placement="top">
            <span className="gw-icon gw-icon-daycab text-blue-700 cursor-help" style={{ fontSize: '16px' }}></span>
          </Tooltip>
        )}
        {order.hazmat === 'Y' && (
          <Tooltip content="Hazmat Required" placement="top">
            <span className="flaticon flaticon-warning text-orange-500 cursor-help" style={{ fontSize: '16px' }}></span>
          </Tooltip>
        )}
        {order.isTank === 'Y' && (
          <Tooltip content="Tank Endorsement Required" placement="top">
            <span className="flaticon flaticon-oil-tank text-purple-600 cursor-help" style={{ fontSize: '18px' }}></span>
          </Tooltip>
        )}
        {order.overweightPermitRequired === 'Y' && (
          <Tooltip content="Overweight Permit Required" placement="top">
            <span className="flaticon flaticon-weight text-heading cursor-help" style={{ fontSize: '16px' }}></span>
          </Tooltip>
        )}
        {order.requiresPpe === 'Y' && (
          <Tooltip content="PPE Required" placement="top">
            <ShieldCheckIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 cursor-help" />
          </Tooltip>
        )}
        {order.isEarly && (
          <Tooltip content="Early Appointment" placement="top">
            <span className="gw-icon gw-icon-early text-heading cursor-help" style={{ fontSize: '16px' }}></span>
          </Tooltip>
        )}
        {order.chassisType?.includes('93K') && (
          <Tooltip content="93K Triaxle Chassis" placement="top">
            <span className="gw-icon cursor-help dark:text-gray-200 text-gray-800" style={{ fontSize: '16px' }}>&#xE9E5;</span>
          </Tooltip>
        )}
        {!order.chassisType?.includes('93K') && order.chassisType?.toLowerCase().includes('triaxle') && (
          <Tooltip content="Triaxle Chassis" placement="top">
            <span className="gw-icon cursor-help dark:text-gray-200 text-gray-800" style={{ fontSize: '16px' }}>&#xE9E1;</span>
          </Tooltip>
        )}
        {order.equipType?.toLowerCase().includes('swing') && (
          <Tooltip content="Swing" placement="top">
            <span className="flaticon flaticon-cargo text-purple-600 cursor-help" style={{ fontSize: '18px' }}></span>
          </Tooltip>
        )}
        {order.warnings && order.warnings.length > 0 && order.warnings.map((w, idx) => (
          <Tooltip key={`warn-${idx}`} content={w.message} placement="top">
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 cursor-help" />
          </Tooltip>
        ))}
      </div>
    );
  };

  // Define columns with sub-row support
  // Memoize to prevent TanTable re-renders causing focus loss.
  // Built once per card: the Imports and Domestic cards share every column and
  // differ only in the driver cell's table type (drag-fill scope) and which
  // board an assignment is tagged with (footer count bucket).
  const buildImportColumns = (
    tableType: 'imports' | 'domestic',
    onDriverAssigned: (pronumber: number, legnum: number, driverUnit: string) => void,
  ): ColumnDef<DispatchPlannerRow>[] => [
    {
      id: 'proLeg',
      accessorFn: (row) => `${row.pronumber}-${row.legnum || 0}`,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'PRO#',
      size: 160,
      cell: ({ row }) => {
        const { pronumber, legnum, isSubRow } = row.original;
        if (isSubRow) {
          return <span className="text-gray-500 pl-4">↳ {pronumber}-{legnum}</span>;
        }
        return (
          <ProLegCell
            pronumber={pronumber}
            legnum={legnum}
            onOpen={() => openOrderModal(row.original)}
            legacyCmsUrl={legacyCmsUrl}
          />
        );
      }
    },
    {
      id: 'rs',
      accessorKey: 'rescheduleCount',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'SF',
      size: 60,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const { rescheduleCount, rescheduleHistory } = row.original;
        if (!rescheduleCount || rescheduleCount === 0) return null;
        return (
          <Tooltip
            content={
              rescheduleHistory && rescheduleHistory.length > 0 ? (
                <div className="text-xs space-y-2 max-w-xs">
                  <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                    Service Failure History ({rescheduleCount})
                  </div>
                  {rescheduleHistory.map((entry, idx) => (
                    <div key={idx} className="space-y-0.5">
                      <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
                      <div className="text-gray-900 font-medium">{entry.reason || 'No reason provided'}</div>
                      <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
                    </div>
                  ))}
                </div>
              ) : null
            }
            placement="right"
          >
            <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${rescheduleCount === 1 ? 'bg-orange-500' : 'bg-red-500'}`}>
              {rescheduleCount}
            </span>
          </Tooltip>
        );
      }
    },
    openTtColumn,
    {
      id: 'live',
      accessorKey: 'isLiveLoad',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Live',
      size: 60,
      // Group header shows the text value ("Live"/"Drop"); field cells keep the ✓/blank checkmark
      cell: ({ row, getValue }) => {
        if (row.getIsGrouped()) return getValue() ? 'Live' : 'Drop';
        return row.original.isSubRow ? null : (row.original.isLiveLoad ? '✓' : '');
      }
    },
    {
      id: 'billToName',
      accessorKey: 'billToName',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Bill To',
      size: 150,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.billToName
    },
    {
      id: 'customerName',
      accessorKey: 'customerName',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Customer',
      size: 150,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.customerName
    },
    {
      id: 'keyAccount',
      accessorKey: 'keyAccount',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Key Account',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.keyAccount
    },
    {
      id: 'dispatcher',
      accessorKey: 'dispatcher',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Dispatcher',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.dispatcher
    },
    {
      id: 'region',
      accessorKey: 'region',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Region',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.region
    },
    {
      id: 'appointmentDate',
      accessorKey: 'appointmentDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Appointment',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.appointmentDate)
    },
    {
      id: 'origType',
      accessorKey: 'origType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Orig Type' },
      header: () => (
        <Tooltip
          content={
            <div className="text-xs">
              <div className="font-semibold mb-1">Leg Type Abbreviations:</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span>D = Delivery</span><span>PU = Pickup</span>
                <span>SO = Stop Off</span><span>T = Terminate</span>
                <span>BT = Bobtail</span><span>CS = Chassis Split</span>
                <span>GR = Grounded</span><span>SC = Scale</span>
                <span>STG = Storage</span><span>TRA = Transload</span>
              </div>
            </div>
          }
          placement="bottom"
        >
          <span className="cursor-help border-b border-dotted border-gray-400">Orig Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legtype ? (
            <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legtype ? (
          <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
        ) : '-';
      }
    },
    {
      id: 'originName',
      accessorFn: (row) => row.isSubRow ? row.legOriginName : row.originName,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Origin',
      size: 180,
      cell: ({ row }) => renderOriginLocationCell(row.original),
    },
    {
      id: 'originCityState',
      accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legOriginCityState : row.originCityState),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Origin City/State',
      size: 150,
    },
    {
      id: 'origApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'origApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destType',
      accessorKey: 'destType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Dest Type' },
      header: () => (
        <Tooltip
          content={
            <div className="text-xs">
              <div className="font-semibold mb-1">Leg Type Abbreviations:</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span>D = Delivery</span><span>PU = Pickup</span>
                <span>SO = Stop Off</span><span>T = Terminate</span>
                <span>BT = Bobtail</span><span>CS = Chassis Split</span>
                <span>GR = Grounded</span><span>SC = Scale</span>
                <span>STG = Storage</span><span>TRA = Transload</span>
              </div>
            </div>
          }
          placement="bottom"
        >
          <span className="cursor-help border-b border-dotted border-gray-400">Dest Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legAction2 ? (
            <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legAction2 ? (
          <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
        ) : '-';
      }
    },
    {
      id: 'destinationName',
      accessorFn: (row) => row.isSubRow ? row.legDestName : row.destinationName,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Destination',
      size: 180,
      cell: ({ row }) => renderDestLocationCell(row.original),
    },
    {
      id: 'destinationCityState',
      accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legDestCityState : row.destinationCityState),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Dest City/State',
      size: 150,
    },
    {
      id: 'destApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'container',
      accessorKey: 'container',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Container',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.container
    },
    {
      id: 'bol',
      accessorKey: 'bol',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Bol/Bkg/Ref',
      size: 140,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.bol
    },
    {
      id: 'size',
      accessorKey: 'size',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Size',
      size: 80,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.size
    },
    {
      id: 'equipType',
      accessorKey: 'equipType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Equip Type',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.equipType
    },
    {
      id: 'chassisId',
      accessorKey: 'chassisId',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Chassis ID',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisId
    },
    {
      id: 'chassisType',
      accessorKey: 'chassisType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Chassis Type',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisType
    },
    {
      id: 'chassisPool',
      accessorKey: 'chassisProvider',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Chassis Pool',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisProvider
    },
    {
      id: 'lfdDate',
      accessorKey: 'lfdDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'LFD',
      size: 80,
      meta: { highlightDateProximity: true },
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.lfdDate)
    },
    {
      id: 'custTargetDate',
      accessorKey: 'custTargetDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Target Date',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.custTargetDate)
    },
    {
      id: 'perDiemDate',
      accessorKey: 'perDiemDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Per Diem',
      size: 80,
      meta: { highlightDateProximity: true },
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.perDiemDate)
    },
    {
      id: 'legType',
      accessorKey: 'legType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Leg Type',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.legType
    },
    {
      id: 'isMultiStop',
      accessorKey: 'isMultiStop',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Multi-Stop',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : (row.original.isMultiStop ? 'Yes' : 'No')
    },
    {
      id: 'hot',
      accessorKey: 'hot',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'HOT',
      size: 70,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.hot
    },
    {
      id: 'hazmat',
      accessorKey: 'hazmat',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Hazmat',
      size: 80,
      meta: { highlightValues: ['Y'] },
      cell: ({ row }) => row.original.isSubRow ? null : row.original.hazmat
    },
    {
      id: 'otr',
      accessorKey: 'otr',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OTR',
      size: 70,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.otr
    },
    {
      id: 'scac',
      accessorKey: 'scac',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'SCAC',
      size: 80,
      cell: ({ row }) => row.original.isSubRow ? null : row.original.scac
    },
    {
      // Total miles across the order's legs on the parent row; the specific
      // leg's miles on sub-rows. accessorFn returns the parent-level total
      // so column sort orders rows by the full move's distance.
      id: 'legMiles',
      accessorFn: (row) => (row.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Miles',
      size: 80,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legMiles != null ? leg.legMiles.toFixed(2) : '-';
        }
        const total = (row.original.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0);
        return total > 0 ? total.toFixed(2) : '-';
      }
    },
    {
      id: 'req',
      accessorKey: 'requirements',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Req',
      size: 120,
      cell: ({ row }) => row.original.isSubRow ? null : renderRequirementIcons(row.original)
    },
    {
      id: 'streetTurnOpportunity',
      accessorKey: 'streetTurnOpportunity',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'ST Opps',
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const list = row.original.streetTurnOpportunity;
        if (!list) return null;
        const count = list.split(',').filter(Boolean).length;
        if (count === 0) return null;
        return (
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/searchresults?q=${encodeURIComponent(list)}&multi=true`, '_blank', 'noopener,noreferrer');
            }}
            title={list}
          >
            {count}
          </button>
        );
      }
    },
    {
      // How long the row has waited on the board for a driver, from the LC
      // finalize stamp (Order.boardSince). The 5s poll re-renders the grid, so
      // the clock stays live without a timer.
      id: 'timeOnBoard',
      accessorFn: (row) => row.boardSince ?? '',
      enableSorting: true,
      enableColumnFilter: false,
      header: 'Time on Board',
      size: 110,
      cell: ({ row }) => {
        if (row.original.isSubRow || row.getIsGrouped()) return null;
        const { display, className, tooltip } = timeOnBoardCell(row.original.boardSince);
        return (
          <Tooltip content={tooltip} placement="top">
            <span className={`cursor-help ${className}`}>{display}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'driverColumn',
      header: 'Driver',
      size: 200,
      minSize: 180,
      maxSize: 220,
      cell: ({ row }) => {
        // Don't show driver selector for sub-rows - driver is assigned at parent level
        if (row.original.isSubRow) return null;
        return (
          <DriverSelectorCell
            key={`${row.original.pronumber}-${row.original.legnum || 0}-${clearResetKey}`}
            pronumber={row.original.pronumber}
            legnum={row.original.legnum || 0}
            driver={(row.original.legs ?? []).find(l => l.legnum === (row.original.legnum || 0))?.driver ?? ''}
            customerName={row.original.customerName}
            originAppointmentDate={row.original.originAppointmentDate}
            destinationAppointmentDate={row.original.destinationAppointmentDate}
            originCoordinates={row.original.originCoordinates}
            destinationCoordinates={row.original.destinationCoordinates}
            originName={row.original.originName}
            destinationName={row.original.destinationName}
            hazmat={row.original.hazmat}
            isDaycab={row.original.isDaycab}
            isTank={row.original.isTank}
            overweightPermitRequired={row.original.overweightPermitRequired}
            onDriverAssigned={onDriverAssigned}
            onValidationChange={(pro, _leg, isQualified) => {
              recordDriverEdit();
              const key = `${pro}-${row.original.legnum || 0}`;
              if (isQualified) {
                setValidImportPros(prev => new Set(prev).add(key));
              } else {
                setValidImportPros(prev => { const next = new Set(prev); next.delete(key); return next; });
              }
            }}
            initialValidationStatus={(() => { const s = parentValidationRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]; return s === 'valid' || s === 'invalid' ? s : null; })()}
            initialRejectionReasons={parentRejectionReasonsRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            parentValidationRef={parentValidationRef}
            parentRejectionReasonsRef={parentRejectionReasonsRef}
            parentDriverInputRef={pendingDriverInputRef}
            dispatchStatus={dispatchStatusesRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            dispatchBlockedReason={blockingTroubleTicketMessage(row.original.openTickets, undefined, legAtOrBeforeRejectedGroup(row.original.legs, row.original.legnum))}
            dispatchWarnings={(() => {
              const warnings: string[] = [];
              const expired = redeliveryExpirationWarning(row.original, row.original.legs || []);
              if (expired) warnings.push(expired);
              if (needsDropReleaseWarning({
                isDrop: row.original.isDrop,
                deliveryLeg: row.original.deliveryLeg,
                dropReleaseDatetime: row.original.dropReleaseDatetime,
                dispatchGroupLegnums: row.original.legs?.map((l: any) => l.legnum) || [],
              })) warnings.push(DROP_RELEASE_WARNING_MESSAGE);
              if (needsNotReceivingWarning({
                moveType: row.original.moveType,
                firstRcvDate: row.original.firstRcvDate,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(NOT_RECEIVING_WARNING_MESSAGE);
              if (needsNotRailbilledWarning({
                warnings: row.original.warnings,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(NOT_RAILBILLED_WARNING_MESSAGE);
              if (needsMissingPreadviseWarning({
                warnings: row.original.warnings,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(MISSING_PREADVISE_WARNING_MESSAGE);
              // Order-level, so no dispatch-group scoping — see openTroubleTicketCheck.
              const openTt = openTroubleTicketWarning(row.original.openTtCount);
              if (openTt) warnings.push(openTt);
              // Likewise order-level: the repair is on the equipment the whole
              // move rides — see openRepairOrderCheck.
              const openRo = openRepairOrderWarning(row.original.openRoCount);
              if (openRo) warnings.push(openRo);
              return warnings;
            })()}
            groupDriverConflictTooltip={describeDispatchGroupDriverConflict(row.original.legs) ?? undefined}
            socket={socket}
            isConnected={isConnected}
            broadcastUpdate={broadcastUpdate}
            activeDriverAssignments={activeDriverAssignments}
            activeDriverTyping={activeDriverTyping}
            currentUser={currentUser}
            // Missing scope dates are auto-filled from another leg in the
            // group at dispatch time, so don't block the driver input here.
            allowMissingDates
            // Drag-to-fill props
            tableType={tableType}
            dragFillActive={dragFillActiveRef.current}
            dragFillTargets={dragFillTargetsRef.current}
            onDragMouseDown={handleDragMouseDown}
            onDragMouseOver={handleDragMouseOver}
            groupMissingDateLegs={(row.original.legs || [])
              .filter(l => l.missingApptDate && l.legnum !== (row.original.legnum || 0))
              .map(l => l.legnum)}
          />
        );
      }
    },
  ];
  const baseColumns = useMemo<ColumnDef<DispatchPlannerRow>[]>(
    () => buildImportColumns('imports', handleDriverAssigned),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDriverAssigned, handleDragMouseDown, handleDragMouseOver, clearResetKey, socket, isConnected, broadcastUpdate, currentUser, openTtColumn],
  );
  const baseDomesticColumns = useMemo<ColumnDef<DispatchPlannerRow>[]>(
    () => buildImportColumns('domestic', handleDomesticDriverAssigned),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDomesticDriverAssigned, handleDragMouseDown, handleDragMouseOver, clearResetKey, socket, isConnected, broadcastUpdate, currentUser, openTtColumn],
  );

  // Attached columns blank on leg sub-rows, matching this board's hand-written
  // order-sourced columns (`isSubRow ? null : value`). An attached value is resolved
  // per-ORDER — true even of the leg-sourced fields, since `d_destapptdate` is the
  // DELIVERY leg specifically, not whichever leg the sub-row happens to be — so
  // printing it against one particular leg would attribute the wrong leg's value.
  const { columns: columns, availableFields: availableFields } =
    useAdditionalFieldColumns<DispatchPlannerRow>(importFields, {
      baseColumns: baseColumns,
      coveredFields: PLANNER_COVERED_FIELDS,
      isSubRow: isPlannerSubRow,
    });
  const { columns: domesticColumns, availableFields: domesticAvailableFields } =
    useAdditionalFieldColumns<DispatchPlannerRow>(domesticFields, {
      baseColumns: baseDomesticColumns,
      coveredFields: PLANNER_COVERED_FIELDS,
      isSubRow: isPlannerSubRow,
    });
  // Note: activeDriverAssignments and activeDriverTyping intentionally excluded to prevent focus loss

  // Exports columns
  const baseExportColumns = useMemo<ColumnDef<DispatchPlannerRow>[]>(() => [
    {
      id: 'proLeg',
      accessorFn: (row) => `${row.pronumber}-${row.legnum || 0}`,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'PRO#',
      size: 160,
      cell: ({ row }) => {
        const { pronumber, legnum, isSubRow } = row.original;
        if (isSubRow) return <span className="text-gray-500 pl-4">↳ {pronumber}-{legnum}</span>;
        return (
          <ProLegCell
            pronumber={pronumber}
            legnum={legnum}
            onOpen={() => openOrderModal(row.original)}
            legacyCmsUrl={legacyCmsUrl}
          />
        );
      }
    },
    {
      id: 'rs',
      header: 'SF',
      size: 60,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const { rescheduleCount, rescheduleHistory } = row.original;
        if (!rescheduleCount || rescheduleCount === 0) return null;
        return (
          <Tooltip
            content={
              rescheduleHistory && rescheduleHistory.length > 0 ? (
                <div className="text-xs space-y-2 max-w-xs">
                  <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                    Service Failure History ({rescheduleCount})
                  </div>
                  {rescheduleHistory.map((entry, idx) => (
                    <div key={idx} className="space-y-0.5">
                      <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
                      <div className="text-gray-900 font-medium">{entry.reason || 'No reason provided'}</div>
                      <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
                    </div>
                  ))}
                </div>
              ) : null
            }
            placement="right"
          >
            <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${rescheduleCount === 1 ? 'bg-orange-500' : 'bg-red-500'}`}>
              {rescheduleCount}
            </span>
          </Tooltip>
        );
      }
    },
    openTtColumn,
    { id: 'live', accessorKey: 'isLiveLoad', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Live', size: 60, cell: ({ row, getValue }) => row.getIsGrouped() ? (getValue() ? 'Live' : 'Drop') : (row.original.isSubRow ? null : (row.original.isLiveLoad ? '✓' : '')) },
    { id: 'billToName', accessorKey: 'billToName', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Bill To', size: 150, cell: ({ row }) => row.original.isSubRow ? null : row.original.billToName },
    { id: 'customerName', accessorKey: 'customerName', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Customer', size: 180, cell: ({ row }) => row.original.isSubRow ? null : row.original.customerName },
    { id: 'keyAccount', accessorKey: 'keyAccount', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Key Account', size: 140, cell: ({ row }) => row.original.isSubRow ? null : row.original.keyAccount },
    { id: 'region', accessorKey: 'region', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Region', size: 100, cell: ({ row }) => row.original.isSubRow ? null : (row.original as any).region },
    {
      id: 'cutoffDatetime',
      accessorKey: 'cutoffDatetime',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Cutoff',
      size: 100,
      meta: { highlightDateProximity: true },
      cell: ({ row }) => row.original.isSubRow ? null : row.original.cutoffDatetime
    },
    {
      id: 'firstRcvDate',
      accessorKey: 'firstRcvDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: '1st Receive',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.firstRcvDate)
    },
    {
      id: 'appointment',
      accessorKey: 'appointmentDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Appointment',
      size: 100,
      cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.appointmentDate)
    },
    {
      id: 'origType',
      accessorKey: 'origType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Orig Type' },
      header: () => (
        <Tooltip
          content={
            <div className="text-xs">
              <div className="font-semibold mb-1">Leg Type Abbreviations:</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span>D = Delivery</span><span>PU = Pickup</span>
                <span>SO = Stop Off</span><span>T = Terminate</span>
                <span>BT = Bobtail</span><span>CS = Chassis Split</span>
                <span>GR = Grounded</span><span>SC = Scale</span>
                <span>STG = Storage</span><span>TRA = Transload</span>
              </div>
            </div>
          }
          placement="bottom"
        >
          <span className="cursor-help border-b border-dotted border-gray-400">Orig Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legtype ? (
            <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legtype ? (
          <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
        ) : '-';
      }
    },
    {
      id: 'originName',
      accessorFn: (row) => row.isSubRow ? row.legOriginName : row.originName,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Origin',
      size: 150,
    },
    {
      id: 'originCityState',
      accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legOriginCityState : row.originCityState),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Origin City/State',
      size: 150,
    },
    {
      id: 'origApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'origApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destType',
      accessorKey: 'destType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Dest Type' },
      header: () => (
        <Tooltip
          content={
            <div className="text-xs">
              <div className="font-semibold mb-1">Leg Type Abbreviations:</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span>D = Delivery</span><span>PU = Pickup</span>
                <span>SO = Stop Off</span><span>T = Terminate</span>
                <span>BT = Bobtail</span><span>CS = Chassis Split</span>
                <span>GR = Grounded</span><span>SC = Scale</span>
                <span>STG = Storage</span><span>TRA = Transload</span>
              </div>
            </div>
          }
          placement="bottom"
        >
          <span className="cursor-help border-b border-dotted border-gray-400">Dest Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legAction2 ? (
            <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legAction2 ? (
          <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
        ) : '-';
      }
    },
    {
      id: 'destinationName',
      accessorFn: (row) => row.isSubRow ? row.legDestName : row.destinationName,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Destination',
      size: 150,
      cell: ({ row }) => renderDestLocationCell(row.original),
    },
    {
      id: 'destinationCityState',
      accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legDestCityState : row.destinationCityState),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Dest City/State',
      size: 150,
    },
    {
      id: 'destApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    { id: 'container', accessorKey: 'container', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Container', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.container },
    { id: 'bol', accessorKey: 'bol', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Bol/Bkg/Ref', size: 140, cell: ({ row }) => row.original.isSubRow ? null : row.original.bol },
    { id: 'size', accessorKey: 'size', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Size', size: 80, cell: ({ row }) => row.original.isSubRow ? null : row.original.size },
    { id: 'chassisId', accessorKey: 'chassisId', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis ID', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisId },
    { id: 'chassisType', accessorKey: 'chassisType', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis Type', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisType },
    { id: 'chassisPool', accessorKey: 'chassisProvider', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis Pool', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisProvider },
    { id: 'isMultiStop', accessorKey: 'isMultiStop', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Multi-Stop', size: 100, cell: ({ row }) => row.original.isSubRow ? null : (row.original.isMultiStop ? 'Yes' : 'No') },
    { id: 'hot', accessorKey: 'hot', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'HOT', size: 60, cell: ({ row }) => row.original.isSubRow ? null : row.original.hot },
    { id: 'hazmat', accessorKey: 'hazmat', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Hazmat', size: 80, meta: { highlightValues: ['Y'] }, cell: ({ row }) => row.original.isSubRow ? null : row.original.hazmat },
    { id: 'otr', accessorKey: 'otr', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'OTR', size: 60, cell: ({ row }) => row.original.isSubRow ? null : row.original.otr },
    { id: 'scac', accessorKey: 'scac', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'SCAC', size: 80, cell: ({ row }) => row.original.isSubRow ? null : row.original.scac },
    {
      // Total miles across the order's legs on the parent row; the specific
      // leg's miles on sub-rows. accessorFn returns the parent-level total
      // so column sort orders rows by the full move's distance.
      id: 'legMiles',
      accessorFn: (row) => (row.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Miles',
      size: 80,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legMiles != null ? leg.legMiles.toFixed(2) : '-';
        }
        const total = (row.original.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0);
        return total > 0 ? total.toFixed(2) : '-';
      }
    },
    { id: 'req', accessorKey: 'requirements', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes' as any, header: 'Req', size: 120, cell: ({ row }) => row.original.isSubRow ? null : renderRequirementIcons(row.original) },
    {
      id: 'streetTurnOpportunity',
      accessorKey: 'streetTurnOpportunity',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'ST Opps',
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const list = row.original.streetTurnOpportunity;
        if (!list) return null;
        const count = list.split(',').filter(Boolean).length;
        if (count === 0) return null;
        return (
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/searchresults?q=${encodeURIComponent(list)}&multi=true`, '_blank', 'noopener,noreferrer');
            }}
            title={list}
          >
            {count}
          </button>
        );
      }
    },
    {
      // How long the row has waited on the board for a driver, from the LC
      // finalize stamp (Order.boardSince). The 5s poll re-renders the grid, so
      // the clock stays live without a timer.
      id: 'timeOnBoard',
      accessorFn: (row) => row.boardSince ?? '',
      enableSorting: true,
      enableColumnFilter: false,
      header: 'Time on Board',
      size: 110,
      cell: ({ row }) => {
        if (row.original.isSubRow || row.getIsGrouped()) return null;
        const { display, className, tooltip } = timeOnBoardCell(row.original.boardSince);
        return (
          <Tooltip content={tooltip} placement="top">
            <span className={`cursor-help ${className}`}>{display}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'driverColumn',
      header: 'Driver',
      size: 200,
      minSize: 180,
      maxSize: 220,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        return (
          <DriverSelectorCell
            key={`${row.original.pronumber}-${row.original.legnum || 0}-${clearResetKey}`}
            pronumber={row.original.pronumber}
            legnum={row.original.legnum || 0}
            driver={(row.original.legs ?? []).find(l => l.legnum === (row.original.legnum || 0))?.driver ?? ''}
            customerName={row.original.customerName}
            originAppointmentDate={row.original.originAppointmentDate}
            destinationAppointmentDate={row.original.destinationAppointmentDate}
            originCoordinates={row.original.originCoordinates}
            destinationCoordinates={row.original.destinationCoordinates}
            originName={row.original.originName}
            destinationName={row.original.destinationName}
            hazmat={row.original.hazmat}
            isDaycab={row.original.isDaycab}
            isTank={row.original.isTank}
            overweightPermitRequired={row.original.overweightPermitRequired}
            onDriverAssigned={handleExportDriverAssigned}
            onValidationChange={(pro, _leg, isQualified) => {
              recordDriverEdit();
              const key = `${pro}-${row.original.legnum || 0}`;
              if (isQualified) {
                setValidExportPros(prev => new Set(prev).add(key));
              } else {
                setValidExportPros(prev => { const next = new Set(prev); next.delete(key); return next; });
              }
            }}
            initialValidationStatus={(() => { const s = parentValidationRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]; return s === 'valid' || s === 'invalid' ? s : null; })()}
            initialRejectionReasons={parentRejectionReasonsRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            parentValidationRef={parentValidationRef}
            parentRejectionReasonsRef={parentRejectionReasonsRef}
            parentDriverInputRef={pendingDriverInputRef}
            dispatchStatus={dispatchStatusesRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            dispatchBlockedReason={blockingTroubleTicketMessage(row.original.openTickets, undefined, legAtOrBeforeRejectedGroup(row.original.legs, row.original.legnum))}
            dispatchWarnings={(() => {
              const warnings: string[] = [];
              const expired = redeliveryExpirationWarning(row.original, row.original.legs || []);
              if (expired) warnings.push(expired);
              if (needsDropReleaseWarning({
                isDrop: row.original.isDrop,
                deliveryLeg: row.original.deliveryLeg,
                dropReleaseDatetime: row.original.dropReleaseDatetime,
                dispatchGroupLegnums: row.original.legs?.map((l: any) => l.legnum) || [],
              })) warnings.push(DROP_RELEASE_WARNING_MESSAGE);
              if (needsNotReceivingWarning({
                moveType: row.original.moveType,
                firstRcvDate: row.original.firstRcvDate,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(NOT_RECEIVING_WARNING_MESSAGE);
              if (needsNotRailbilledWarning({
                warnings: row.original.warnings,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(NOT_RAILBILLED_WARNING_MESSAGE);
              if (needsMissingPreadviseWarning({
                warnings: row.original.warnings,
                dispatchGroupLegs: row.original.legs || [],
              })) warnings.push(MISSING_PREADVISE_WARNING_MESSAGE);
              // Order-level, so no dispatch-group scoping — see openTroubleTicketCheck.
              const openTt = openTroubleTicketWarning(row.original.openTtCount);
              if (openTt) warnings.push(openTt);
              // Likewise order-level: the repair is on the equipment the whole
              // move rides — see openRepairOrderCheck.
              const openRo = openRepairOrderWarning(row.original.openRoCount);
              if (openRo) warnings.push(openRo);
              return warnings;
            })()}
            groupDriverConflictTooltip={describeDispatchGroupDriverConflict(row.original.legs) ?? undefined}
            socket={socket}
            isConnected={isConnected}
            broadcastUpdate={broadcastUpdate}
            activeDriverAssignments={activeDriverAssignments}
            activeDriverTyping={activeDriverTyping}
            currentUser={currentUser}
            // Missing scope dates are auto-filled from another leg in the
            // group at dispatch time, so don't block the driver input here.
            allowMissingDates
            tableType="exports"
            dragFillActive={dragFillActiveRef.current}
            dragFillTargets={dragFillTargetsRef.current}
            onDragMouseDown={handleDragMouseDown}
            onDragMouseOver={handleDragMouseOver}
            groupMissingDateLegs={(row.original.legs || [])
              .filter(l => l.missingApptDate && l.legnum !== (row.original.legnum || 0))
              .map(l => l.legnum)}
          />
        );
      }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [handleExportDriverAssigned, handleDragMouseDown, handleDragMouseOver, clearResetKey, socket, isConnected, broadcastUpdate, currentUser, openTtColumn]);

  const { columns: exportColumns, availableFields: exportAvailableFields } =
    useAdditionalFieldColumns<DispatchPlannerRow>(exportFields, {
      baseColumns: baseExportColumns,
      coveredFields: PLANNER_COVERED_FIELDS,
      isSubRow: isPlannerSubRow,
    });

  // Drop released columns (mirrors imports with dropReleaseDatetime column)
  const baseDropReleasedColumns = useMemo<ColumnDef<DispatchPlannerRow>[]>(() => [
    {
      id: 'proLeg',
      accessorFn: (row) => `${row.pronumber}-${row.legnum || 0}`,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'PRO#',
      size: 160,
      cell: ({ row }) => {
        const { pronumber, legnum, isSubRow } = row.original;
        if (isSubRow) return <span className="text-gray-500 pl-4">↳ {pronumber}-{legnum}</span>;
        return (
          <ProLegCell
            pronumber={pronumber}
            legnum={legnum}
            onOpen={() => openOrderModal(row.original)}
            legacyCmsUrl={legacyCmsUrl}
          />
        );
      }
    },
    {
      id: 'rs',
      accessorKey: 'rescheduleCount',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'SF',
      size: 60,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const { rescheduleCount, rescheduleHistory } = row.original;
        if (!rescheduleCount || rescheduleCount === 0) return null;
        return (
          <Tooltip
            content={
              rescheduleHistory && rescheduleHistory.length > 0 ? (
                <div className="text-xs space-y-2 max-w-xs">
                  <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">Service Failure History ({rescheduleCount})</div>
                  {rescheduleHistory.map((entry, idx) => (
                    <div key={idx} className="space-y-0.5">
                      <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
                      <div className="text-gray-900 font-medium">{entry.reason || 'No reason provided'}</div>
                      <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
                    </div>
                  ))}
                </div>
              ) : null
            }
            placement="right"
          >
            <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${rescheduleCount === 1 ? 'bg-orange-500' : 'bg-red-500'}`}>
              {rescheduleCount}
            </span>
          </Tooltip>
        );
      }
    },
    openTtColumn,
    { id: 'moveType', accessorKey: 'moveType', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes' as any, header: 'Move Type', size: 100, cell: ({ row }) => row.original.isSubRow ? null : row.original.moveType },
    { id: 'billToName', accessorKey: 'billToName', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Bill To', size: 150, cell: ({ row }) => row.original.isSubRow ? null : row.original.billToName },
    { id: 'customerName', accessorKey: 'customerName', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Customer', size: 150, cell: ({ row }) => row.original.isSubRow ? null : row.original.customerName },
    { id: 'keyAccount', accessorKey: 'keyAccount', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Key Account', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.keyAccount },
    { id: 'region', accessorKey: 'region', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Region', size: 100, cell: ({ row }) => row.original.isSubRow ? null : row.original.region },
    {
      id: 'dropReleaseDatetime',
      // accessorFn returns date-only so grouping groups by date, not datetime
      accessorFn: (row) => row.dropReleaseDatetime ? String(row.dropReleaseDatetime).split(' ')[0] : '',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Drop Released',
      size: 130,
      // Grouped rows show date-only; regular rows show full datetime
      cell: ({ row, getValue }) => {
        if (row.getIsGrouped()) return getValue();
        return row.original.isSubRow ? null : row.original.dropReleaseDatetime;
      }
    },
    { id: 'cutoffDatetime', accessorKey: 'cutoffDatetime', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Cutoff', size: 100, meta: { highlightDateProximity: true }, cell: ({ row }) => row.original.isSubRow ? null : row.original.cutoffDatetime },
    { id: 'lfdDate', accessorKey: 'lfdDate', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'LFD', size: 80, meta: { highlightDateProximity: true }, cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.lfdDate) },
    { id: 'custTargetDate', accessorKey: 'custTargetDate', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Target Date', size: 100, cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.custTargetDate) },
    { id: 'perDiemDate', accessorKey: 'perDiemDate', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Per Diem', size: 80, meta: { highlightDateProximity: true }, cell: ({ row }) => row.original.isSubRow ? null : formatDateShort(row.original.perDiemDate) },
    {
      id: 'origType',
      accessorKey: 'origType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Orig Type' },
      header: () => (
        <Tooltip content={<div className="text-xs"><div className="font-semibold mb-1">Leg Type Abbreviations:</div><div className="grid grid-cols-2 gap-x-3 gap-y-0.5"><span>D = Delivery</span><span>PU = Pickup</span><span>SO = Stop Off</span><span>T = Terminate</span><span>BT = Bobtail</span><span>CS = Chassis Split</span><span>GR = Grounded</span><span>SC = Scale</span><span>STG = Storage</span><span>TRA = Transload</span></div></div>} placement="bottom">
          <span className="cursor-help border-b border-dotted border-gray-400">Orig Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legtype ? (
            <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legtype ? (
          <Tooltip content={leg.legtype} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legtype)}</span></Tooltip>
        ) : '-';
      }
    },
    { id: 'originName', accessorFn: (row) => row.isSubRow ? row.legOriginName : row.originName, enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Origin', size: 180, cell: ({ row }) => renderOriginLocationCell(row.original) },
    { id: 'originCityState', accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legOriginCityState : row.originCityState), enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Origin City/State', size: 150 },
    {
      id: 'origApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'origApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.origApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'OA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destType',
      accessorKey: 'destType',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { groupLabel: 'Dest Type' },
      header: () => (
        <Tooltip content={<div className="text-xs"><div className="font-semibold mb-1">Leg Type Abbreviations:</div><div className="grid grid-cols-2 gap-x-3 gap-y-0.5"><span>D = Delivery</span><span>PU = Pickup</span><span>SO = Stop Off</span><span>T = Terminate</span><span>BT = Bobtail</span><span>CS = Chassis Split</span><span>GR = Grounded</span><span>SC = Scale</span><span>STG = Storage</span><span>TRA = Transload</span></div></div>} placement="bottom">
          <span className="cursor-help border-b border-dotted border-gray-400">Dest Type</span>
        </Tooltip>
      ),
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legAction2 ? (
            <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
          ) : null;
        }
        const leg = row.original.legs?.[0];
        return leg?.legAction2 ? (
          <Tooltip content={leg.legAction2} placement="right"><span className="cursor-help">{abbreviateLegType(leg.legAction2)}</span></Tooltip>
        ) : '-';
      }
    },
    {
      id: 'destinationName',
      accessorFn: (row) => row.isSubRow ? row.legDestName : row.destinationName,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Destination',
      size: 180,
      cell: ({ row }) => renderDestLocationCell(row.original),
    },
    { id: 'destinationCityState', accessorFn: (row) => cityStateOnly(row.isSubRow ? row.legDestCityState : row.destinationCityState), enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Dest City/State', size: 150 },
    {
      id: 'destApptTime1',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime1 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA Start',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    {
      id: 'destApptTime2',
      accessorFn: (row) => {
        const leg = row.isSubRow
          ? row.legs?.find(l => l.legnum === row.legnum)
          : row.legs?.[0];
        return leg?.destApptTime2 || '';
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'DA End',
      size: 90,
      cell: ({ getValue }) => (getValue() as string) || '-',
      sortingFn: emptyLastStringSort,
    },
    { id: 'container', accessorKey: 'container', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Container', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.container },
    { id: 'bol', accessorKey: 'bol', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Bol/Bkg/Ref', size: 140, cell: ({ row }) => row.original.isSubRow ? null : row.original.bol },
    { id: 'size', accessorKey: 'size', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Size', size: 80, cell: ({ row }) => row.original.isSubRow ? null : row.original.size },
    { id: 'equipType', accessorKey: 'equipType', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Equip Type', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.equipType },
    { id: 'chassisId', accessorKey: 'chassisId', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis ID', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisId },
    { id: 'chassisType', accessorKey: 'chassisType', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis Type', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisType },
    { id: 'chassisPool', accessorKey: 'chassisProvider', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Chassis Pool', size: 120, cell: ({ row }) => row.original.isSubRow ? null : row.original.chassisProvider },
    { id: 'hot', accessorKey: 'hot', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'HOT', size: 70, cell: ({ row }) => row.original.isSubRow ? null : row.original.hot },
    { id: 'hazmat', accessorKey: 'hazmat', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'Hazmat', size: 80, meta: { highlightValues: ['Y'] }, cell: ({ row }) => row.original.isSubRow ? null : row.original.hazmat },
    { id: 'otr', accessorKey: 'otr', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'OTR', size: 70, cell: ({ row }) => row.original.isSubRow ? null : row.original.otr },
    { id: 'scac', accessorKey: 'scac', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes', header: 'SCAC', size: 80, cell: ({ row }) => row.original.isSubRow ? null : row.original.scac },
    {
      // Total miles across the order's legs on the parent row; the specific
      // leg's miles on sub-rows. accessorFn returns the parent-level total
      // so column sort orders rows by the full move's distance.
      id: 'legMiles',
      accessorFn: (row) => (row.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0),
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Miles',
      size: 80,
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.isSubRow) {
          const leg = row.original.legs?.find(l => l.legnum === row.original.legnum);
          return leg?.legMiles != null ? leg.legMiles.toFixed(2) : '-';
        }
        const total = (row.original.legs ?? []).reduce((sum, l) => sum + (l.legMiles ?? 0), 0);
        return total > 0 ? total.toFixed(2) : '-';
      }
    },
    { id: 'req', accessorKey: 'requirements', enableSorting: true, enableColumnFilter: true, filterFn: 'arrIncludes' as any, header: 'Req', size: 120, cell: ({ row }) => row.original.isSubRow ? null : renderRequirementIcons(row.original) },
    {
      id: 'streetTurnOpportunity',
      accessorKey: 'streetTurnOpportunity',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'ST Opps',
      size: 80,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        const list = row.original.streetTurnOpportunity;
        if (!list) return null;
        const count = list.split(',').filter(Boolean).length;
        if (count === 0) return null;
        return (
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/searchresults?q=${encodeURIComponent(list)}&multi=true`, '_blank', 'noopener,noreferrer');
            }}
            title={list}
          >
            {count}
          </button>
        );
      }
    },
    {
      // Editable appt-date column. EVERY parent row gets an inline
      // DateTimePicker (via ApptDateCell, which subscribes to picked dates
      // through context) — there is no read-only variant.
      //
      // It used to render read-only text unless some leg had a blank appt
      // date, which made the 46-of-595 fully-dated rows uneditable (prod
      // 2026-09-02) and, worse, was pointless even where it did unlock: a
      // pick only ever filled blank sides, so on a fully-dated group the
      // typed date was silently discarded. A pick now overrides every appt
      // date in the dispatch group (see resolveLegApptDates).
      //
      // Safe to overwrite here specifically because the Drop Released board
      // is legs dispatchers schedule themselves — the listing gates on
      // `l.legnum > o.deliveryleg`, so a customer delivery appointment is
      // never in range (verified: 0 of 595 live rows had a dispatch group
      // reaching the delivery leg).
      //
      // The cell renderer does NOT close over pickedApptDates — that goes
      // through context — so date picks don't rebuild this columns array
      // and disturb in-progress driver validation in neighboring cells.
      id: 'appointmentDate',
      accessorKey: 'appointmentDate',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      header: 'Appt Date',
      size: 160,
      meta: {
        bulkEdit: { type: 'date' } satisfies BulkEditMeta,
      },
      cell: ({ row, column }) => {
        if (row.original.isSubRow) return null;
        return (
          <ApptDateCell
            pronumber={row.original.pronumber}
            legnum={row.original.legnum || 0}
            width={column.getSize()}
            currentIso={apptDateToIso(row.original.appointmentDate)}
          />
        );
      }
    },
    {
      // How long the row has waited on the board for a driver, from the LC
      // finalize stamp (Order.boardSince). The 5s poll re-renders the grid, so
      // the clock stays live without a timer.
      id: 'timeOnBoard',
      accessorFn: (row) => row.boardSince ?? '',
      enableSorting: true,
      enableColumnFilter: false,
      header: 'Time on Board',
      size: 110,
      cell: ({ row }) => {
        if (row.original.isSubRow || row.getIsGrouped()) return null;
        const { display, className, tooltip } = timeOnBoardCell(row.original.boardSince);
        return (
          <Tooltip content={tooltip} placement="top">
            <span className={`cursor-help ${className}`}>{display}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'driverColumn',
      header: 'Driver',
      size: 200,
      minSize: 180,
      maxSize: 220,
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        return (
          <DriverSelectorCell
            key={`${row.original.pronumber}-${row.original.legnum || 0}-${clearResetKey}`}
            pronumber={row.original.pronumber}
            legnum={row.original.legnum || 0}
            driver={(row.original.legs ?? []).find(l => l.legnum === (row.original.legnum || 0))?.driver ?? ''}
            customerName={row.original.customerName}
            originAppointmentDate={row.original.originAppointmentDate}
            destinationAppointmentDate={row.original.destinationAppointmentDate}
            originCoordinates={row.original.originCoordinates}
            destinationCoordinates={row.original.destinationCoordinates}
            originName={row.original.originName}
            destinationName={row.original.destinationName}
            hazmat={row.original.hazmat}
            isDaycab={row.original.isDaycab}
            isTank={row.original.isTank}
            overweightPermitRequired={row.original.overweightPermitRequired}
            onDriverAssigned={handleDropReleasedDriverAssigned}
            allowMissingDates={true}
            onValidationChange={(pro, _leg, isQualified) => {
              recordDriverEdit();
              const key = `${pro}-${row.original.legnum || 0}`;
              if (isQualified) {
                setValidDropReleasePros(prev => new Set(prev).add(key));
              } else {
                setValidDropReleasePros(prev => { const next = new Set(prev); next.delete(key); return next; });
              }
            }}
            initialValidationStatus={(() => { const s = parentValidationRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]; return s === 'valid' || s === 'invalid' ? s : null; })()}
            initialRejectionReasons={parentRejectionReasonsRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            parentValidationRef={parentValidationRef}
            parentRejectionReasonsRef={parentRejectionReasonsRef}
            parentDriverInputRef={pendingDriverInputRef}
            dispatchStatus={dispatchStatusesRef.current[`${row.original.pronumber}-${row.original.legnum || 0}`]}
            groupDriverConflictTooltip={describeDispatchGroupDriverConflict(row.original.legs) ?? undefined}
            socket={socket}
            isConnected={isConnected}
            broadcastUpdate={broadcastUpdate}
            activeDriverAssignments={activeDriverAssignments}
            activeDriverTyping={activeDriverTyping}
            currentUser={currentUser}
            tableType="dropreleased"
            dragFillActive={dragFillActiveRef.current}
            dragFillTargets={dragFillTargetsRef.current}
            onDragMouseDown={handleDragMouseDown}
            onDragMouseOver={handleDragMouseOver}
          />
        );
      }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [handleDropReleasedDriverAssigned, handleDragMouseDown, handleDragMouseOver, clearResetKey, socket, isConnected, broadcastUpdate, currentUser, openTtColumn]);

  const { columns: dropReleasedColumns, availableFields: dropReleasedAvailableFields } =
    useAdditionalFieldColumns<DispatchPlannerRow>(dropReleasedFields, {
      baseColumns: baseDropReleasedColumns,
      coveredFields: PLANNER_COVERED_FIELDS,
      isSubRow: isPlannerSubRow,
    });

  const { columns: dropNotDispatchedColumns, availableFields: dropNotDispatchedAvailableFields } =
    useAdditionalFieldColumns<DispatchPlannerRow>(dropNotDispatchedFields, {
      baseColumns: baseDropReleasedColumns,
      coveredFields: PLANNER_COVERED_FIELDS,
      isSubRow: isPlannerSubRow,
    });

  // Transform tabData to work with DispatchPlannerRow (Imports + Domestic share the shape)
  const toRowDateTabs = (tabs: TabData<Order>[]): TabData<DispatchPlannerRow>[] => {
    return tabs.map(tab => ({
      ...tab,
      filterFn: (row: DispatchPlannerRow) => {
        // Sub-rows inherit parent's tab membership
        if (row.isSubRow) return true;
        if (!row.appointmentDate) return false;
        const orderDate = row.appointmentDate.split(' ')[0];
        if (tab.id === 'past-due') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          return orderDate < todayStr;
        }
        return orderDate === tab.id;
      }
    }));
  };
  const transformedDateTabs = useMemo<TabData<DispatchPlannerRow>[]>(() => toRowDateTabs(dateTabs), [dateTabs]);
  const transformedDomesticDateTabs = useMemo<TabData<DispatchPlannerRow>[]>(() => toRowDateTabs(domesticDateTabs), [domesticDateTabs]);

  const transformedExportDateTabs = useMemo<TabData<DispatchPlannerRow>[]>(() => {
    return exportDateTabs.map(tab => ({
      ...tab,
      filterFn: (row: DispatchPlannerRow) => {
        if (row.isSubRow) return true;
        if (tab.id === 'pool') return row.cutoffDatetime === 'POOL';
        if (tab.id === 'rolling') return row.cutoffDatetime === 'ROLLING';
        if (tab.id === 'past-due') {
          if (!row.appointmentDate) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          return row.appointmentDate.split(' ')[0] < todayStr;
        }
        // Strip "exp-" prefix for date comparison
        const dateId = tab.id.replace('exp-', '');
        if (!row.appointmentDate) return false;
        return row.appointmentDate.split(' ')[0] === dateId;
      }
    }));
  }, [exportDateTabs]);

  const hasAnyFilter = selectedDispatchers.length > 0 || selectedRegions.length > 0 || selectedOTRs.length > 0 || selectedCustomers.length > 0 || selectedRequirements.length > 0 || selectedLive.length > 0 || selectedSize.length > 0 || selectedContainerType.length > 0 || selectedChassisType.length > 0;

  // Stable context value: identity changes only when pickedApptDates does, so
  // ApptDateCells re-render on date picks but driver cells (which don't use
  // this context) stay untouched.
  const pickedApptDatesCtx = useMemo(
    () => ({ pickedApptDates, setPickedApptDates }),
    [pickedApptDates],
  );

  return (
    <PickedApptDatesContext.Provider value={pickedApptDatesCtx}>
    <div>
      <FilterBar label="Filters:">
        <OrderDispatcherFilter
          value={selectedDispatchers}
          onChange={setSelectedDispatchers}
          className="min-w-[140px]"
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={otrOptions}
          value={selectedOTRs}
          onChange={setSelectedOTRs}
          placeholder="OTR"
          showClearOption={true}
          clearOptionLabel="All"
          size="sm"
          className="min-w-[80px]"
        />
        <MultiSelect
          options={customerOptions}
          value={selectedCustomers}
          onChange={setSelectedCustomers}
          placeholder="All Customers"
          showClearOption={true}
          clearOptionLabel="All Customers"
          enableSearch={true}
          searchPlaceholder="Search customers..."
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={requirementOptions}
          value={selectedRequirements}
          onChange={setSelectedRequirements}
          placeholder="Requirements"
          showClearOption={true}
          clearOptionLabel="All"
          size="sm"
          className="min-w-[120px]"
        />
        <MultiSelect
          options={liveOptions}
          value={selectedLive}
          onChange={setSelectedLive}
          placeholder="Live"
          showClearOption={true}
          clearOptionLabel="All"
          size="sm"
          className="min-w-[80px]"
        />
        <MultiSelect
          options={sizeOptions}
          value={selectedSize}
          onChange={setSelectedSize}
          placeholder="Size"
          showClearOption={true}
          clearOptionLabel="All"
          enableSearch={true}
          searchPlaceholder="Search sizes..."
          size="sm"
          className="min-w-[100px]"
        />
        <MultiSelect
          options={containerTypeOptions}
          value={selectedContainerType}
          onChange={setSelectedContainerType}
          placeholder="Container Type"
          showClearOption={true}
          clearOptionLabel="All"
          enableSearch={true}
          searchPlaceholder="Search types..."
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={chassisTypeOptions}
          value={selectedChassisType}
          onChange={setSelectedChassisType}
          placeholder="Chassis Type"
          showClearOption={true}
          clearOptionLabel="All"
          enableSearch={true}
          searchPlaceholder="Search types..."
          size="sm"
          className="min-w-[140px]"
        />
        {hasAnyFilter && (
          <button
            onClick={() => {
              setSelectedDispatchers([]);
              setSelectedRegions([]);
              setSelectedOTRs([]);
              setSelectedCustomers([]);
              setSelectedRequirements([]);
              setSelectedLive([]);
              setSelectedSize([]);
              setSelectedContainerType([]);
              setSelectedChassisType([]);
            }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Clear Filters
          </button>
        )}
      </FilterBar>

      <div className="p-6">
      <div ref={gridRef} className="grid-stack">
        <div
          className="grid-stack-item"
          gs-id="imports"
          gs-x={layout.imports.x}
          gs-y={layout.imports.y}
          gs-w={layout.imports.w}
          gs-h={layout.imports.h}
        >
          <div className="grid-stack-item-content">
            <TanTable
              data={transformedOrders}
              columns={columns}
              availableFields={availableFields}
              additionalFields={importFields.additionalFields}
              onAdditionalFieldsChange={importFields.onAdditionalFieldsChange}
              trackViewedColumnId="proLeg"
              getViewedKey={(r) => r.pronumber}
              loading={loading}
              cardTitle="Imports"
              searchPrefix={
                <LegTypeFilters
                  active={legTypeFiltersImports}
                  onToggle={(key) =>
                    setLegTypeFiltersImports(prev =>
                      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                    )
                  }
                />
              }
              cardLocked={cardLocked.imports}
              onToggleCardLock={(locked) => handleToggleCardLock('imports', locked)}
              collapsed={importsCollapsed}
              onToggleCollapse={() => handleToggleCollapse('imports', importsCollapsed, setImportsCollapsed)}
              showTabs={true}
              tabData={transformedDateTabs}
              multiTabSelect={true}
              activeTabs={activeTabs}
              onActiveTabsChange={setActiveTabs}
              allowTabFieldSelection={false}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSearch={true}
              enableZebraStriping={true}
              paginationStyle="compactWithButtons"
              cardHeight={cardHeight}
              onCardHeightChange={setCardHeight}
              fillContainer={true}
              lockedColumns={[
                { id: 'proLeg', side: 'left' },
                { id: 'driverColumn', side: 'right' }
              ]}
              settingsKey="dispatch-planner"
              defaultColumnVisibility={PLANNER_DEFAULT_HIDDEN_COLUMNS}
              defaultSettings={plannerDefaults['dispatch-planner']}
              canEditDefaults={true}
              onSaveDefaults={handleSaveDefaults}
              getSubRows={(row) => row.subLegs}

              getRelatedRows={getDispatchGroupRelatedRows}
              showIndicator={true}
              getIndicatorInfo={getKeyAccountIndicator}
              alwaysExpanded={true}
            />
          </div>
        </div>

        <div
          className="grid-stack-item"
          gs-id="domestic"
          gs-x={layout.domestic.x}
          gs-y={layout.domestic.y}
          gs-w={layout.domestic.w}
          gs-h={layout.domestic.h}
        >
          <div className="grid-stack-item-content">
            <TanTable
              data={transformedDomestic}
              columns={domesticColumns}
              availableFields={domesticAvailableFields}
              additionalFields={domesticFields.additionalFields}
              onAdditionalFieldsChange={domesticFields.onAdditionalFieldsChange}
              trackViewedColumnId="proLeg"
              getViewedKey={(r) => r.pronumber}
              loading={loading}
              cardTitle="Domestic"
              searchPrefix={
                <LegTypeFilters
                  active={legTypeFiltersDomestic}
                  onToggle={(key) =>
                    setLegTypeFiltersDomestic(prev =>
                      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                    )
                  }
                />
              }
              cardLocked={cardLocked.domestic}
              onToggleCardLock={(locked) => handleToggleCardLock('domestic', locked)}
              collapsed={domesticCollapsed}
              onToggleCollapse={() => handleToggleCollapse('domestic', domesticCollapsed, setDomesticCollapsed)}
              showTabs={true}
              tabData={transformedDomesticDateTabs}
              multiTabSelect={true}
              activeTabs={activeDomesticTabs}
              onActiveTabsChange={setActiveDomesticTabs}
              allowTabFieldSelection={false}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSearch={true}
              enableZebraStriping={true}
              paginationStyle="compactWithButtons"
              cardHeight={domesticCardHeight}
              onCardHeightChange={setDomesticCardHeight}
              fillContainer={true}
              lockedColumns={[
                { id: 'proLeg', side: 'left' },
                { id: 'driverColumn', side: 'right' }
              ]}
              settingsKey="dispatch-planner-domestic"
              defaultColumnVisibility={PLANNER_DEFAULT_HIDDEN_COLUMNS}
              defaultSettings={plannerDefaults['dispatch-planner-domestic']}
              canEditDefaults={true}
              onSaveDefaults={handleSaveDefaults}
              getSubRows={(row) => row.subLegs}
              getRelatedRows={getDispatchGroupRelatedRows}
              showIndicator={true}
              getIndicatorInfo={getKeyAccountIndicator}
              alwaysExpanded={true}
            />
          </div>
        </div>

        <div
          className="grid-stack-item"
          gs-id="exports"
          gs-x={layout.exports.x}
          gs-y={layout.exports.y}
          gs-w={layout.exports.w}
          gs-h={layout.exports.h}
        >
          <div className="grid-stack-item-content">
            <TanTable
              data={transformedExports}
              columns={exportColumns}
              availableFields={exportAvailableFields}
              additionalFields={exportFields.additionalFields}
              onAdditionalFieldsChange={exportFields.onAdditionalFieldsChange}
              trackViewedColumnId="proLeg"
              getViewedKey={(r) => r.pronumber}
              loading={exportsLoading}
              cardTitle="Exports"
              searchPrefix={
                <LegTypeFilters
                  active={legTypeFiltersExports}
                  onToggle={(key) =>
                    setLegTypeFiltersExports(prev =>
                      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                    )
                  }
                />
              }
              cardLocked={cardLocked.exports}
              onToggleCardLock={(locked) => handleToggleCardLock('exports', locked)}
              collapsed={exportsCollapsed}
              onToggleCollapse={() => handleToggleCollapse('exports', exportsCollapsed, setExportsCollapsed)}
              showTabs={true}
              tabData={transformedExportDateTabs}
              multiTabSelect={true}
              activeTabs={activeExportTabs}
              onActiveTabsChange={setActiveExportTabs}
              allowTabFieldSelection={false}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSearch={true}
              enableZebraStriping={true}
              paginationStyle="compactWithButtons"
              cardHeight={exportsCardHeight}
              onCardHeightChange={setExportsCardHeight}
              fillContainer={true}
              lockedColumns={[
                { id: 'proLeg', side: 'left' },
                { id: 'driverColumn', side: 'right' }
              ]}
              settingsKey="dispatch-planner-exports"
              defaultColumnVisibility={PLANNER_DEFAULT_HIDDEN_COLUMNS}
              defaultSettings={plannerDefaults['dispatch-planner-exports']}
              canEditDefaults={true}
              onSaveDefaults={handleSaveDefaults}
              getSubRows={(row) => row.subLegs}

              getRelatedRows={getDispatchGroupRelatedRows}
              showIndicator={true}
              getIndicatorInfo={getKeyAccountIndicator}
              alwaysExpanded={true}
            />
          </div>
        </div>

        <div
          className="grid-stack-item"
          gs-id="dropReleased"
          gs-x={layout.dropReleased.x}
          gs-y={layout.dropReleased.y}
          gs-w={layout.dropReleased.w}
          gs-h={layout.dropReleased.h}
        >
          <div className="grid-stack-item-content">
            <TanTable
              data={readyDropReleasedRows}
              columns={dropReleasedColumns}
              availableFields={dropReleasedAvailableFields}
              additionalFields={dropReleasedFields.additionalFields}
              onAdditionalFieldsChange={dropReleasedFields.onAdditionalFieldsChange}
              trackViewedColumnId="proLeg"
              getViewedKey={(r) => r.pronumber}
              loading={dropReleasedLoading}
              cardTitle="Drop Released"
              cardLocked={cardLocked.dropReleased}
              onToggleCardLock={(locked) => handleToggleCardLock('dropReleased', locked)}
              collapsed={dropReleasedCollapsed}
              onToggleCollapse={() => handleToggleCollapse('dropReleased', dropReleasedCollapsed, setDropReleasedCollapsed)}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSearch={true}
              enableZebraStriping={true}
              paginationStyle="compactWithButtons"
              cardHeight={dropReleasedCardHeight}
              onCardHeightChange={setDropReleasedCardHeight}
              fillContainer={true}
              lockedColumns={[
                { id: 'proLeg', side: 'left' },
                { id: 'appointmentDate', side: 'right' },
                { id: 'driverColumn', side: 'right' }
              ]}
              settingsKey="dispatch-planner-dropreleased"
              defaultColumnVisibility={PLANNER_DEFAULT_HIDDEN_COLUMNS}
              defaultSettings={plannerDefaults['dispatch-planner-dropreleased']}
              canEditDefaults={true}
              onSaveDefaults={handleSaveDefaults}
              getSubRows={(row) => row.subLegs}
              onBulkEdit={handleDropReleasedBulkEdit}
              getRelatedRows={getDispatchGroupRelatedRows}
              showIndicator={true}
              getIndicatorInfo={getKeyAccountIndicator}
              alwaysExpanded={true}
            />
          </div>
        </div>

        <div
          className="grid-stack-item"
          gs-id="dropNotDispatched"
          gs-x={layout.dropNotDispatched.x}
          gs-y={layout.dropNotDispatched.y}
          gs-w={layout.dropNotDispatched.w}
          gs-h={layout.dropNotDispatched.h}
        >
          <div className="grid-stack-item-content">
            <TanTable
              data={notDispatchedDropReleasedRows}
              columns={dropNotDispatchedColumns}
              availableFields={dropNotDispatchedAvailableFields}
              additionalFields={dropNotDispatchedFields.additionalFields}
              onAdditionalFieldsChange={dropNotDispatchedFields.onAdditionalFieldsChange}
              trackViewedColumnId="proLeg"
              getViewedKey={(r) => r.pronumber}
              loading={dropReleasedLoading}
              cardTitle="Drop Released Not Dispatched"
              cardLocked={cardLocked.dropNotDispatched}
              onToggleCardLock={(locked) => handleToggleCardLock('dropNotDispatched', locked)}
              collapsed={dropNotDispatchedCollapsed}
              onToggleCollapse={() => handleToggleCollapse('dropNotDispatched', dropNotDispatchedCollapsed, setDropNotDispatchedCollapsed)}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSearch={true}
              enableZebraStriping={true}
              paginationStyle="compactWithButtons"
              cardHeight={dropNotDispatchedCardHeight}
              onCardHeightChange={setDropNotDispatchedCardHeight}
              fillContainer={true}
              lockedColumns={[
                { id: 'proLeg', side: 'left' },
                { id: 'appointmentDate', side: 'right' },
                { id: 'driverColumn', side: 'right' }
              ]}
              settingsKey="dispatch-planner-drop-not-dispatched"
              defaultColumnVisibility={PLANNER_DEFAULT_HIDDEN_COLUMNS}
              defaultSettings={plannerDefaults['dispatch-planner-drop-not-dispatched']}
              canEditDefaults={true}
              onSaveDefaults={handleSaveDefaults}
              getSubRows={(row) => row.subLegs}
              onBulkEdit={handleDropReleasedBulkEdit}
              getRelatedRows={getDispatchGroupRelatedRows}
              showIndicator={true}
              getIndicatorInfo={getKeyAccountIndicator}
              alwaysExpanded={true}
            />
          </div>
        </div>

        <div
          className="grid-stack-item"
          gs-id="notSent"
          gs-x={layout.notSent.x}
          gs-y={layout.notSent.y}
          gs-w={layout.notSent.w}
          gs-h={layout.notSent.h}
        >
          <div className="grid-stack-item-content">
            <NotSentToGwiMoveCard
              data={notSentData}
              totalCount={notSentTotalCount}
              loading={notSentLoading}
              error={notSentError}
              collapsed={notSentCollapsed}
              onToggleCollapse={() => handleToggleCollapse('notSent', notSentCollapsed, setNotSentCollapsed)}
              cardLocked={cardLocked.notSent}
              onToggleCardLock={(locked) => handleToggleCardLock('notSent', locked)}
              cardHeight={notSentCardHeight}
              setCardHeight={setNotSentCardHeight}
              fillContainer={true}
              onProNumberClick={openOrderModalByPronumber}
              currentUserOid={permissions?.user_id}
            />
          </div>
        </div>
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={closeOrderModal}
      />
      </div>
      {pendingDragFill && (
        <DragFillWarningModal
          warnedPros={pendingDragFill.warnedPros}
          totalCells={pendingDragFill.targets.length}
          onConfirm={() => {
            performDragFill(pendingDragFill.targets, pendingDragFill.sourceValue);
            setPendingDragFill(null);
          }}
          onCancel={() => setPendingDragFill(null)}
        />
      )}
    </div>
    </PickedApptDatesContext.Provider>
  );
}

export default function DispatchPlanner() {
  return (
    <ViewedProsProvider>
      <DispatchPlannerContent />
    </ViewedProsProvider>
  );
}

